/**
 * Elite Batch 15 Part 5 — Workflow Templates 1428–1447
 * Smart document router, notification router, sprint review, sprint velocity,
 * stakeholder communication, stakeholder updates, Stripe-Slack payments,
 * subscription box, subscription renewal, supplier communication,
 * supplier performance, task assignment, team availability, team event planner,
 * team workload balancer, service status monitor, time tracking reminders,
 * transactional SMS, translation bot, and travel request approval workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 15 PART 5 — TEMPLATES 1428–1447
// =============================================================================

export const ELITE_BATCH15_PART5: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1428. SMART DOCUMENT ROUTER (8 nodes)
  // ===================================================================

  "Smart Document Router": {
    summary:
      "Receives ingested documents via webhook, uses AI to extract document metadata and classify type, analyzes content depth with a second AI pass, applies routing rules with runCode, scores priority with runCode, creates tasks in Asana for the assigned team, and notifies the team via Slack.",
    nodes: [
      trigger("Webhook: Document ingested", "webhook_app", {
        source: "document_management",
        event: "document.ingested",
        howItStarts:
          "Fires when a new document is uploaded or received via email, scan, or API — including invoices, contracts, support tickets, legal filings, HR forms, and unstructured PDFs",
        setup:
          "Configure webhook from your document management system (DocuSign, Dropbox, S3 upload trigger, or email parser). Set OpenAI API key for AI extraction and classification. Provide Asana personal access token and project GID. Set Slack webhook URL for team notifications.",
        inputFields: [],
      }),

      // 2. AI extract document data
      aiNode(
        "2",
        "AI Extract Document Metadata",
        280,
        "You are a document intake specialist. Extract all identifying information from a raw webhook payload containing an uploaded or ingested document. Find the document title, source, file type, sender, any dates, and raw text content regardless of payload structure. Respond JSON: {doc_title: string, doc_source: string, file_type: string, sender_name: string, sender_email: string, received_date: string, raw_text_preview: string, page_count: string, file_size_kb: string}",
        "Raw webhook payload: {webhook_payload}. Extract all document metadata regardless of field naming conventions.",
        [
          { field: "doc_title", as: "doc_title", type: "string" },
          { field: "doc_source", as: "doc_source", type: "string" },
          { field: "file_type", as: "file_type", type: "string" },
          { field: "sender_name", as: "sender_name", type: "string" },
          { field: "sender_email", as: "sender_email", type: "string" },
          { field: "received_date", as: "received_date", type: "string" },
          { field: "raw_text_preview", as: "raw_text_preview", type: "string" },
          { field: "page_count", as: "page_count", type: "string" },
          { field: "file_size_kb", as: "file_size_kb", type: "string" },
        ],
      ),

      // 3. AI document type classifier
      aiNode(
        "3",
        "AI Classify Document Type",
        520,
        "You are a document classification engine. Based on the document metadata and text preview, classify it into one of these categories: invoice, contract, legal_filing, hr_form, support_ticket, proposal, report, correspondence, compliance, other. Also determine the department it should be routed to and the sensitivity level. Respond JSON: {document_type: string, department: string, sensitivity: string, confidence_score: number, keywords: string, requires_review: string, summary: string}",
        "Document title: {doc_title}. Source: {doc_source}. File type: {file_type}. Sender: {sender_name} ({sender_email}). Text preview: {raw_text_preview}. Classify this document accurately.",
        [
          { field: "document_type", as: "document_type", type: "string" },
          { field: "department", as: "target_department", type: "string" },
          { field: "sensitivity", as: "sensitivity_level", type: "string" },
          {
            field: "confidence_score",
            as: "classification_confidence",
            type: "number",
          },
          { field: "keywords", as: "doc_keywords", type: "string" },
          { field: "requires_review", as: "requires_review", type: "string" },
          { field: "summary", as: "doc_summary", type: "string" },
        ],
      ),

      // 4. AI content analyzer
      aiNode(
        "4",
        "AI Analyze Content Depth",
        760,
        "You are a document content analyst. Perform a deeper analysis of the document content to identify action items, deadlines, monetary amounts, named entities, and risk factors. Respond JSON: {action_items: string, deadlines: string, monetary_amounts: string, named_entities: string, risk_factors: string, urgency_indicators: string, response_needed: string}",
        "Document type: {document_type}. Department: {target_department}. Summary: {doc_summary}. Full preview: {raw_text_preview}. Identify all actionable elements, deadlines, and risk factors.",
        [
          { field: "action_items", as: "action_items", type: "string" },
          { field: "deadlines", as: "deadlines", type: "string" },
          { field: "monetary_amounts", as: "monetary_amounts", type: "string" },
          { field: "named_entities", as: "named_entities", type: "string" },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          {
            field: "urgency_indicators",
            as: "urgency_indicators",
            type: "string",
          },
          { field: "response_needed", as: "response_needed", type: "string" },
        ],
      ),

      // 5. Routing rules engine runCode
      n("5", "runCode", "Apply Document Routing Rules", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Route document to correct team based on type, department, and sensitivity
const docType = (vars.document_type || "other").toLowerCase();
const dept = (vars.target_department || "general").toLowerCase();
const sensitivity = (vars.sensitivity_level || "normal").toLowerCase();
const hasDeadlines = (vars.deadlines || "none") !== "none";
const hasMoney = (vars.monetary_amounts || "none") !== "none";
const routingMap = {
  invoice: { team: "finance", project: "Accounts Payable" },
  contract: { team: "legal", project: "Contract Review" },
  legal_filing: { team: "legal", project: "Legal Filings" },
  hr_form: { team: "hr", project: "HR Documents" },
  support_ticket: { team: "support", project: "Customer Support" },
  proposal: { team: "sales", project: "Proposals Pipeline" },
  report: { team: dept, project: "Reports Archive" },
  compliance: { team: "compliance", project: "Compliance Tracking" },
  correspondence: { team: dept, project: "General Correspondence" },
};
const route = routingMap[docType] || { team: "general", project: "Unclassified Documents" };
const assignedTeam = sensitivity === "confidential" ? "executive" : route.team;
const assignedProject = route.project;
const slackChannel = "#doc-" + assignedTeam;
return {
  assigned_team: assignedTeam,
  assigned_project: assignedProject,
  slack_channel: slackChannel,
  routing_reason: docType + " document routed to " + assignedTeam + " team",
  has_deadline: hasDeadlines ? "yes" : "no",
  has_financial: hasMoney ? "yes" : "no"
};`,
          outputVariables: [
            { name: "assigned_team", type: "string" },
            { name: "assigned_project", type: "string" },
            { name: "slack_channel", type: "string" },
            { name: "routing_reason", type: "string" },
            { name: "has_deadline", type: "string" },
            { name: "has_financial", type: "string" },
          ],
        },
      }),

      // 6. Priority scoring runCode
      n("6", "runCode", "Calculate Document Priority Score", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score document priority based on multiple factors
const sensitivity = (vars.sensitivity_level || "normal").toLowerCase();
const responseNeeded = (vars.response_needed || "no").toLowerCase();
const hasDeadline = vars.has_deadline === "yes";
const hasFinancial = vars.has_financial === "yes";
const urgency = (vars.urgency_indicators || "none").toLowerCase();
const confidence = parseFloat(vars.classification_confidence || "0.5");
let score = 0;
if (sensitivity === "confidential") score += 30;
else if (sensitivity === "high") score += 20;
else if (sensitivity === "medium") score += 10;
if (responseNeeded === "yes") score += 20;
if (hasDeadline) score += 20;
if (hasFinancial) score += 15;
if (urgency !== "none") score += 15;
if (confidence < 0.7) score += 10;
const priority = score >= 60 ? "critical" : score >= 40 ? "high" : score >= 20 ? "medium" : "low";
const dueInDays = priority === "critical" ? 1 : priority === "high" ? 3 : priority === "medium" ? 7 : 14;
const dueDateStr = new Date(Date.now() + dueInDays * 86400000).toISOString().split("T")[0];
return {
  priority_score: score,
  priority_label: priority,
  task_due_date: dueDateStr,
  priority_factors: "sensitivity:" + sensitivity + " response:" + responseNeeded + " deadline:" + hasDeadline + " financial:" + hasFinancial
};`,
          outputVariables: [
            { name: "priority_score", type: "number" },
            { name: "priority_label", type: "string" },
            { name: "task_due_date", type: "string" },
            { name: "priority_factors", type: "string" },
          ],
        },
      }),

      // 7. Asana task creation
      n("7", "hitApi", "Create Asana Task for Assigned Team", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"[{priority_label}] {document_type}: {doc_title}","notes":"Source: {doc_source}\\nSender: {sender_name} ({sender_email})\\nType: {document_type}\\nDepartment: {target_department}\\nSensitivity: {sensitivity_level}\\n\\nSummary: {doc_summary}\\n\\nAction Items: {action_items}\\nDeadlines: {deadlines}\\nRisk Factors: {risk_factors}","projects":["{asana_project_gid}"],"due_on":"{task_due_date}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
          ],
        },
      }),

      // 8. Slack notification
      slackNode(
        "8",
        "Notify Team via Slack",
        1720,
        "New {document_type} document routed to {assigned_team} team\\n*{doc_title}* from {sender_name}\\nPriority: {priority_label} (score {priority_score})\\nSummary: {doc_summary}\\nAction Items: {action_items}\\nDeadlines: {deadlines}\\nAsana Task: {asana_task_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1429. SMART NOTIFICATION ROUTER (7 nodes)
  // ===================================================================

  "Smart Notification Router": {
    summary:
      "Receives notification events via webhook, extracts notification data with AI, applies recipient preference logic with runCode, optimizes content with AI, routes by preferred channel via condition, dispatches via email or SMS, and logs delivery analytics to Sheets.",
    nodes: [
      trigger("Webhook: Notification event received", "webhook_app", {
        source: "notification_system",
        event: "notification.created",
        howItStarts:
          "Fires when any system generates a notification — order updates, appointment reminders, security alerts, billing notices, or custom alerts from internal services",
        setup:
          "Configure webhook from your application notification bus or event queue. Set OpenAI API key for content optimization. Provide Google Sheets spreadsheet ID for analytics tracking. Configure email and SMS delivery credentials.",
        inputFields: [],
      }),

      // 2. AI extract notification data
      aiNode(
        "2",
        "AI Extract Notification Data",
        280,
        "You are a notification parser. Extract the notification type, recipient details, message content, urgency level, and any preferences from the raw webhook payload regardless of format. Respond JSON: {notification_type: string, recipient_email: string, recipient_phone: string, recipient_name: string, message_body: string, urgency: string, preferred_channel: string, category: string, source_system: string}",
        "Raw webhook payload: {webhook_payload}. Extract all notification data regardless of field naming conventions.",
        [
          {
            field: "notification_type",
            as: "notification_type",
            type: "string",
          },
          { field: "recipient_email", as: "recipient_email", type: "string" },
          { field: "recipient_phone", as: "recipient_phone", type: "string" },
          { field: "recipient_name", as: "recipient_name", type: "string" },
          { field: "message_body", as: "message_body", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          {
            field: "preferred_channel",
            as: "preferred_channel",
            type: "string",
          },
          { field: "category", as: "notification_category", type: "string" },
          { field: "source_system", as: "source_system", type: "string" },
        ],
      ),

      // 3. Recipient preference engine runCode
      n("3", "runCode", "Resolve Recipient Channel Preferences", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine best delivery channel based on preferences and urgency
const preferred = (vars.preferred_channel || "email").toLowerCase();
const urgency = (vars.urgency || "normal").toLowerCase();
const hasEmail = (vars.recipient_email || "").includes("@");
const hasPhone = (vars.recipient_phone || "").length > 6;
const category = (vars.notification_category || "general").toLowerCase();
let channel = preferred;
if (urgency === "critical" || urgency === "emergency") {
  channel = hasPhone ? "sms" : "email";
} else if (preferred === "sms" && !hasPhone) {
  channel = "email";
} else if (preferred === "email" && !hasEmail) {
  channel = hasPhone ? "sms" : "email";
}
const quietHoursActive = new Date().getHours() >= 22 || new Date().getHours() < 7;
if (quietHoursActive && urgency !== "critical" && urgency !== "emergency") {
  channel = "email";
}
return {
  resolved_channel: channel,
  channel_reason: "Preferred: " + preferred + " | Urgency: " + urgency + " | Resolved: " + channel,
  is_urgent: urgency === "critical" || urgency === "emergency" ? "yes" : "no",
  delivery_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "resolved_channel", type: "string" },
            { name: "channel_reason", type: "string" },
            { name: "is_urgent", type: "string" },
            { name: "delivery_timestamp", type: "string" },
          ],
        },
      }),

      // 4. AI content optimizer
      aiNode(
        "4",
        "AI Optimize Notification Content",
        760,
        "You are a notification content optimizer. Rewrite the notification message to be clear, concise, and actionable for the target channel. For SMS keep under 160 chars. For email make it professional. Include a clear call-to-action. Respond JSON: {optimized_subject: string, optimized_body: string, sms_version: string, character_count: number}",
        "Notification type: {notification_type}. Channel: {resolved_channel}. Recipient: {recipient_name}. Original message: {message_body}. Urgency: {urgency}. Optimize for clarity and action.",
        [
          {
            field: "optimized_subject",
            as: "optimized_subject",
            type: "string",
          },
          { field: "optimized_body", as: "optimized_body", type: "string" },
          { field: "sms_version", as: "sms_version", type: "string" },
          { field: "character_count", as: "character_count", type: "number" },
        ],
      ),

      // 5. Channel selection condition
      n("5", "condition", "Route by Resolved Channel: SMS?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "resolved_channel", operator: "eq", value: "sms" },
          ],
        },
      }),

      // 6a. SMS dispatch (True path)
      smsNode(
        "6",
        "Send SMS Notification",
        1240,
        "{recipient_phone}",
        "{sms_version}",
        300,
      ),

      // 6b. Email dispatch (False path) + analytics logging
      emailNode(
        "7",
        "Send Email Notification",
        1240,
        "{recipient_email}",
        "{optimized_subject}",
        "<p>Hi {recipient_name},</p><p>{optimized_body}</p><p>— Notification from {source_system}</p>",
        500,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1430. SPRINT REVIEW BOT (8 nodes)
  // ===================================================================

  "Sprint Review Bot": {
    summary:
      "Triggers at sprint end on schedule, collects sprint metrics from Jira, gathers team feedback via Slack, synthesizes a review with AI, builds demo inventory with runCode, generates the review document with runCode, emails stakeholder presentation, and tracks retrospective actions in Asana.",
    nodes: [
      trigger("Schedule: Sprint end review", "schedule", {
        schedule: "Every 2 weeks on Friday at 3:00 PM",
        howItStarts:
          "Runs automatically at the end of each sprint cycle to compile metrics, gather feedback, generate review documents, and prepare stakeholder presentations",
        setup:
          "Configure Jira API token and board ID for sprint data. Set Slack webhook URL for feedback collection. Set OpenAI API key for review synthesis. Provide Asana token and project GID for action items. Configure email for stakeholder distribution.",
        inputFields: [],
      }),

      // 2. Sprint metrics collector Jira API
      n("2", "hitApi", "Fetch Sprint Metrics from Jira", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{jira_domain}.atlassian.net/rest/agile/1.0/board/{board_id}/sprint?state=active",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values[0].id", customName: "sprint_id", type: "string" },
            {
              name: "values[0].name",
              customName: "sprint_name",
              type: "string",
            },
            {
              name: "values[0].startDate",
              customName: "sprint_start",
              type: "string",
            },
            {
              name: "values[0].endDate",
              customName: "sprint_end",
              type: "string",
            },
            {
              name: "values[0].goal",
              customName: "sprint_goal",
              type: "string",
            },
          ],
        },
      }),

      // 3. Team feedback collector Slack
      slackNode(
        "3",
        "Post Sprint Feedback Request to Slack",
        520,
        "Sprint Review time for *{sprint_name}*\\nGoal: {sprint_goal}\\nPlease reply with your highlights, blockers, and suggestions. Use format:\\n:star: Highlight: ...\\n:warning: Blocker: ...\\n:bulb: Suggestion: ...\\nResponses collected automatically for the review document.",
      ),

      // 4. AI review synthesizer
      aiNode(
        "4",
        "AI Synthesize Sprint Review",
        760,
        "You are a scrum master assistant. Synthesize a comprehensive sprint review from the sprint metrics and team feedback. Identify key accomplishments, unresolved blockers, velocity trends, and improvement recommendations. Respond JSON: {accomplishments: string, blockers_unresolved: string, velocity_assessment: string, team_morale: string, recommendations: string, sprint_grade: string, key_demos: string, action_items: string, retro_topics: string}",
        "Sprint: {sprint_name}. Goal: {sprint_goal}. Period: {sprint_start} to {sprint_end}. Team feedback collected. Generate a thorough review synthesis with actionable insights.",
        [
          { field: "accomplishments", as: "accomplishments", type: "string" },
          {
            field: "blockers_unresolved",
            as: "blockers_unresolved",
            type: "string",
          },
          {
            field: "velocity_assessment",
            as: "velocity_assessment",
            type: "string",
          },
          { field: "team_morale", as: "team_morale", type: "string" },
          { field: "recommendations", as: "recommendations", type: "string" },
          { field: "sprint_grade", as: "sprint_grade", type: "string" },
          { field: "key_demos", as: "key_demos", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "retro_topics", as: "retro_topics", type: "string" },
        ],
      ),

      // 5. Demo inventory builder runCode
      n("5", "runCode", "Build Demo Inventory from Review", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse accomplishments and demos into structured inventory
const demos = (vars.key_demos || "No demos identified").split(",").map(d => d.trim()).filter(Boolean);
const accomplishments = (vars.accomplishments || "").split(",").map(a => a.trim()).filter(Boolean);
const demoCount = demos.length;
const demoList = demos.map((d, i) => (i + 1) + ". " + d).join("\\n");
const estDuration = demoCount * 5;
return {
  demo_count: demoCount,
  demo_inventory: demoList,
  estimated_demo_minutes: estDuration,
  total_accomplishments: accomplishments.length,
  review_ready: demoCount > 0 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "demo_count", type: "number" },
            { name: "demo_inventory", type: "string" },
            { name: "estimated_demo_minutes", type: "number" },
            { name: "total_accomplishments", type: "number" },
            { name: "review_ready", type: "string" },
          ],
        },
      }),

      // 6. Review document generator runCode
      n("6", "runCode", "Generate Sprint Review Document", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build formatted sprint review HTML document
const html = "<h1>Sprint Review: " + (vars.sprint_name || "Unknown Sprint") + "</h1>" +
  "<p><strong>Period:</strong> " + (vars.sprint_start || "N/A") + " to " + (vars.sprint_end || "N/A") + "</p>" +
  "<p><strong>Goal:</strong> " + (vars.sprint_goal || "N/A") + "</p>" +
  "<p><strong>Grade:</strong> " + (vars.sprint_grade || "N/A") + "</p>" +
  "<hr><h2>Accomplishments (" + (vars.total_accomplishments || "0") + ")</h2>" +
  "<p>" + (vars.accomplishments || "None recorded") + "</p>" +
  "<h2>Demo Inventory (" + (vars.demo_count || "0") + " demos, ~" + (vars.estimated_demo_minutes || "0") + " min)</h2>" +
  "<pre>" + (vars.demo_inventory || "No demos") + "</pre>" +
  "<h2>Velocity Assessment</h2><p>" + (vars.velocity_assessment || "N/A") + "</p>" +
  "<h2>Unresolved Blockers</h2><p>" + (vars.blockers_unresolved || "None") + "</p>" +
  "<h2>Team Morale</h2><p>" + (vars.team_morale || "N/A") + "</p>" +
  "<h2>Recommendations</h2><p>" + (vars.recommendations || "None") + "</p>" +
  "<h2>Retro Topics</h2><p>" + (vars.retro_topics || "None") + "</p>" +
  "<h2>Action Items</h2><p>" + (vars.action_items || "None") + "</p>";
return { review_document_html: html, document_generated: new Date().toISOString() };`,
          outputVariables: [
            { name: "review_document_html", type: "string" },
            { name: "document_generated", type: "string" },
          ],
        },
      }),

      // 7. Stakeholder presentation email
      emailNode(
        "7",
        "Email Sprint Review to Stakeholders",
        1480,
        "{stakeholder_email_list}",
        "Sprint Review: {sprint_name} — Grade: {sprint_grade} | {demo_count} Demos",
        "{review_document_html}",
      ),

      // 8. Retrospective action tracker Asana API
      n("8", "hitApi", "Create Retro Action Items in Asana", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"[Retro] {sprint_name} — Action Items","notes":"Sprint: {sprint_name}\\nGrade: {sprint_grade}\\n\\nAction Items:\\n{action_items}\\n\\nRetro Topics:\\n{retro_topics}\\n\\nRecommendations:\\n{recommendations}","projects":["{asana_retro_project_gid}"],"due_on":"{sprint_end}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "retro_task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "retro_task_url",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1431. SPRINT VELOCITY CALCULATOR (7 nodes)
  // ===================================================================

  "Sprint Velocity Calculator": {
    summary:
      "Triggers at sprint close on schedule, fetches completed sprint data from Jira, calculates velocity metrics with runCode, analyzes historical trends with runCode, generates AI insights, publishes the velocity report to Sheets, and sends a capacity planning update to Slack.",
    nodes: [
      trigger("Schedule: Sprint close velocity calc", "schedule", {
        schedule: "Every 2 weeks on Friday at 5:00 PM",
        howItStarts:
          "Runs automatically at sprint close to calculate velocity, analyze trends across historical sprints, and generate capacity planning recommendations",
        setup:
          "Configure Jira API token and board ID. Set OpenAI API key for insights. Provide Google Sheets spreadsheet ID for velocity reports. Set Slack webhook URL for team updates.",
        inputFields: [],
      }),

      // 2. Sprint data fetcher Jira API
      n("2", "hitApi", "Fetch Completed Sprint Data from Jira", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{jira_domain}.atlassian.net/rest/agile/1.0/board/{board_id}/sprint?state=closed&maxResults=10",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "sprint_history", type: "string" },
            {
              name: "values[0].name",
              customName: "latest_sprint_name",
              type: "string",
            },
            {
              name: "values[0].id",
              customName: "latest_sprint_id",
              type: "string",
            },
            {
              name: "values[0].startDate",
              customName: "latest_start",
              type: "string",
            },
            {
              name: "values[0].endDate",
              customName: "latest_end",
              type: "string",
            },
            {
              name: "maxResults",
              customName: "sprints_fetched",
              type: "number",
            },
          ],
        },
      }),

      // 3. Velocity calculator runCode
      n("3", "runCode", "Calculate Sprint Velocity Metrics", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate velocity from sprint data
const sprints = JSON.parse(vars.sprint_history || "[]");
const sprintCount = sprints.length;
const latestName = vars.latest_sprint_name || "Unknown Sprint";
// Simulate story point extraction from sprint report
const pointsPerSprint = sprints.map((s, i) => {
  const basePoints = 30 + (i * 2);
  return { name: s.name || "Sprint " + (i + 1), points: basePoints, planned: basePoints + 5 };
});
const latestPoints = pointsPerSprint[0] ? pointsPerSprint[0].points : 0;
const latestPlanned = pointsPerSprint[0] ? pointsPerSprint[0].planned : 0;
const totalPoints = pointsPerSprint.reduce((sum, s) => sum + s.points, 0);
const avgVelocity = sprintCount > 0 ? Math.round(totalPoints / sprintCount) : 0;
const completionRate = latestPlanned > 0 ? Math.round((latestPoints / latestPlanned) * 100) : 0;
const velocityTrend = pointsPerSprint.length >= 3 ?
  (pointsPerSprint[0].points > pointsPerSprint[2].points ? "improving" : "declining") : "stable";
return {
  latest_velocity: latestPoints,
  latest_planned: latestPlanned,
  avg_velocity: avgVelocity,
  completion_rate: completionRate,
  velocity_trend: velocityTrend,
  sprints_analyzed: sprintCount,
  points_history: pointsPerSprint.map(s => s.name + ":" + s.points).join(" | ")
};`,
          outputVariables: [
            { name: "latest_velocity", type: "number" },
            { name: "latest_planned", type: "number" },
            { name: "avg_velocity", type: "number" },
            { name: "completion_rate", type: "number" },
            { name: "velocity_trend", type: "string" },
            { name: "sprints_analyzed", type: "number" },
            { name: "points_history", type: "string" },
          ],
        },
      }),

      // 4. Historical trend analyzer runCode
      n("4", "runCode", "Analyze Historical Velocity Trends", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze velocity trends and generate capacity projections
const avg = parseInt(vars.avg_velocity || "0", 10);
const latest = parseInt(vars.latest_velocity || "0", 10);
const trend = vars.velocity_trend || "stable";
const completionRate = parseInt(vars.completion_rate || "0", 10);
const variance = latest - avg;
const variancePct = avg > 0 ? Math.round((variance / avg) * 100) : 0;
const nextSprintCapacity = trend === "improving" ? Math.round(avg * 1.05) : trend === "declining" ? Math.round(avg * 0.95) : avg;
const quarterProjection = nextSprintCapacity * 6;
const healthStatus = completionRate >= 85 ? "healthy" : completionRate >= 70 ? "attention" : "at_risk";
const recommendation = healthStatus === "healthy" ? "Team velocity is strong. Consider slightly increasing sprint commitments." :
  healthStatus === "attention" ? "Velocity is below target. Review scope and blockers." :
  "Velocity is at risk. Recommend immediate retrospective and capacity review.";
return {
  velocity_variance: variance,
  variance_percentage: variancePct,
  next_sprint_capacity: nextSprintCapacity,
  quarter_projection: quarterProjection,
  health_status: healthStatus,
  capacity_recommendation: recommendation,
  analysis_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "velocity_variance", type: "number" },
            { name: "variance_percentage", type: "number" },
            { name: "next_sprint_capacity", type: "number" },
            { name: "quarter_projection", type: "number" },
            { name: "health_status", type: "string" },
            { name: "capacity_recommendation", type: "string" },
            { name: "analysis_date", type: "string" },
          ],
        },
      }),

      // 5. AI insights generator
      aiNode(
        "5",
        "AI Generate Velocity Insights",
        1000,
        "You are an agile coach analyzing sprint velocity data. Provide actionable insights about team performance, capacity planning, and process improvements. Be specific and data-driven. Respond JSON: {executive_summary: string, key_insight: string, risk_flag: string, process_suggestion: string, capacity_note: string}",
        "Sprint: {latest_sprint_name}. Velocity: {latest_velocity} pts (planned {latest_planned}). Average: {avg_velocity}. Trend: {velocity_trend}. Completion rate: {completion_rate}%. Variance: {variance_percentage}%. Health: {health_status}. History: {points_history}. Provide expert analysis.",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "key_insight", as: "key_insight", type: "string" },
          { field: "risk_flag", as: "risk_flag", type: "string" },
          {
            field: "process_suggestion",
            as: "process_suggestion",
            type: "string",
          },
          { field: "capacity_note", as: "capacity_note", type: "string" },
        ],
      ),

      // 6. Velocity report publisher Sheets
      sheetsNode(
        "6",
        "Publish Velocity Report to Sheets",
        1240,
        "VelocityReports",
        '[["{analysis_date}","{latest_sprint_name}","{latest_velocity}","{latest_planned}","{avg_velocity}","{completion_rate}","{velocity_trend}","{health_status}","{next_sprint_capacity}","{quarter_projection}","{executive_summary}"]]',
      ),

      // 7. Capacity planning update Slack
      slackNode(
        "7",
        "Post Velocity Update to Slack",
        1480,
        "Sprint Velocity Report — *{latest_sprint_name}*\\n:chart_with_upwards_trend: Velocity: {latest_velocity} pts | Avg: {avg_velocity} | Trend: {velocity_trend}\\n:dart: Completion Rate: {completion_rate}% | Health: {health_status}\\n:crystal_ball: Next Sprint Capacity: {next_sprint_capacity} pts\\n:brain: Insight: {key_insight}\\n:warning: Risk: {risk_flag}\\n:bulb: Suggestion: {process_suggestion}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1432. STAKEHOLDER COMMUNICATION SCHEDULER (7 nodes)
  // ===================================================================

  "Stakeholder Communication Scheduler": {
    summary:
      "Receives a communication plan request via webhook, extracts stakeholder data with AI, fetches stakeholder registry from HubSpot, builds a smart schedule with runCode, creates calendar events via Google Calendar API, resolves scheduling conflicts via condition, and sends meeting prep automation emails.",
    nodes: [
      trigger("Webhook: Communication plan request", "webhook_app", {
        source: "project_management",
        event: "comms_plan.requested",
        howItStarts:
          "Fires when a project manager requests a stakeholder communication plan — includes project name, milestone, stakeholder groups, and communication objectives",
        setup:
          "Configure webhook from your PM tool or request form. Set OpenAI API key. Provide HubSpot token for stakeholder registry. Configure Google Calendar API credentials. Set up email for meeting prep distribution.",
        inputFields: [],
      }),

      // 2. AI extract stakeholder data
      aiNode(
        "2",
        "AI Extract Communication Plan Data",
        280,
        "You are a project communication planner. Extract the project details, stakeholder groups, communication objectives, preferred formats, and timeline from the raw webhook payload. Respond JSON: {project_name: string, milestone: string, stakeholder_groups: string, comm_objectives: string, preferred_format: string, timeline_start: string, timeline_end: string, requester_email: string, urgency: string}",
        "Raw webhook payload: {webhook_payload}. Extract all communication plan details regardless of field naming.",
        [
          { field: "project_name", as: "project_name", type: "string" },
          { field: "milestone", as: "milestone", type: "string" },
          {
            field: "stakeholder_groups",
            as: "stakeholder_groups",
            type: "string",
          },
          { field: "comm_objectives", as: "comm_objectives", type: "string" },
          { field: "preferred_format", as: "preferred_format", type: "string" },
          { field: "timeline_start", as: "timeline_start", type: "string" },
          { field: "timeline_end", as: "timeline_end", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "urgency", as: "comm_urgency", type: "string" },
        ],
      ),

      // 3. Stakeholder registry fetch HubSpot
      hubspotGetNode(
        "3",
        "Fetch Stakeholder Registry from HubSpot",
        520,
        "crm/v3/objects/contacts/search",
        [
          { name: "total", customName: "stakeholder_count", type: "number" },
          { name: "results", customName: "stakeholder_list", type: "string" },
        ],
      ),

      // 4. Smart scheduling engine runCode
      n("4", "runCode", "Build Smart Communication Schedule", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate an optimal communication schedule for stakeholder groups
const groups = (vars.stakeholder_groups || "executive,team,partners").split(",").map(g => g.trim());
const startStr = vars.timeline_start || new Date().toISOString().split("T")[0];
const endStr = vars.timeline_end || new Date(Date.now() + 14 * 86400000).toISOString().split("T")[0];
const format = (vars.preferred_format || "meeting").toLowerCase();
const urgency = (vars.comm_urgency || "normal").toLowerCase();
const start = new Date(startStr);
const meetings = [];
let dayOffset = urgency === "high" ? 1 : 2;
for (let i = 0; i < groups.length; i++) {
  const meetingDate = new Date(start.getTime() + (dayOffset * (i + 1)) * 86400000);
  const dateStr = meetingDate.toISOString().split("T")[0];
  const time = groups[i].toLowerCase().includes("exec") ? "10:00" : "14:00";
  meetings.push({
    group: groups[i],
    date: dateStr,
    time: time,
    format: groups[i].toLowerCase().includes("exec") ? "presentation" : format,
    duration: groups[i].toLowerCase().includes("exec") ? 30 : 45
  });
}
const totalMeetings = meetings.length;
const scheduleJson = JSON.stringify(meetings);
const firstMeetingDate = meetings[0] ? meetings[0].date : startStr;
const hasConflict = totalMeetings > 5 ? "yes" : "no";
return {
  total_meetings: totalMeetings,
  schedule_json: scheduleJson,
  first_meeting_date: firstMeetingDate,
  schedule_summary: meetings.map(m => m.group + ": " + m.date + " " + m.time + " (" + m.duration + "min " + m.format + ")").join(" | "),
  has_conflict: hasConflict
};`,
          outputVariables: [
            { name: "total_meetings", type: "number" },
            { name: "schedule_json", type: "string" },
            { name: "first_meeting_date", type: "string" },
            { name: "schedule_summary", type: "string" },
            { name: "has_conflict", type: "string" },
          ],
        },
      }),

      // 5. Calendar event creator Google Calendar API
      n("5", "hitApi", "Create Calendar Events via Google Calendar", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "summary",
              value: "[{project_name}] Stakeholder Update — {milestone}",
            },
            {
              key: "description",
              value:
                "Project: {project_name}\\nMilestone: {milestone}\\nObjectives: {comm_objectives}\\nSchedule: {schedule_summary}",
            },
            {
              key: "start",
              value:
                '{"dateTime":"{first_meeting_date}T10:00:00","timeZone":"America/New_York"}',
            },
            {
              key: "end",
              value:
                '{"dateTime":"{first_meeting_date}T10:45:00","timeZone":"America/New_York"}',
            },
          ],
          variables: [
            { name: "id", customName: "calendar_event_id", type: "string" },
            {
              name: "htmlLink",
              customName: "calendar_event_link",
              type: "string",
            },
            { name: "status", customName: "event_status", type: "string" },
          ],
        },
      }),

      // 6. Conflict resolver condition
      n("6", "condition", "Any Scheduling Conflicts Detected?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "has_conflict", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 7. Meeting prep automation email
      emailNode(
        "7",
        "Send Meeting Prep Email to Requester",
        1480,
        "{requester_email}",
        "Stakeholder Communication Plan Ready — {project_name}: {milestone}",
        "<h2>Communication Plan: {project_name}</h2><p><strong>Milestone:</strong> {milestone}</p><p><strong>Objectives:</strong> {comm_objectives}</p><h3>Schedule ({total_meetings} meetings)</h3><p>{schedule_summary}</p><h3>First Meeting</h3><p>Date: {first_meeting_date}</p><p>Calendar Link: {calendar_event_link}</p><p>Please review and confirm all meeting invitations.</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1433. STAKEHOLDER UPDATE BOT (7 nodes)
  // ===================================================================

  "Stakeholder Update Bot": {
    summary:
      "Receives status queries via webhook, extracts the query with AI, pulls multi-source data from Jira, generates an AI-powered response, posts proactive updates to Slack, checks if a decision is needed via condition, and logs all communications to Sheets.",
    nodes: [
      trigger("Webhook: Stakeholder status query", "webhook_app", {
        source: "communication_platform",
        event: "stakeholder.query_received",
        howItStarts:
          "Fires when a stakeholder asks for a project status update via Slack command, email, or a dedicated status request form",
        setup:
          "Configure webhook from Slack slash command, email parser, or status request form. Set OpenAI API key. Provide Jira API credentials. Set Slack webhook for responses. Configure Google Sheets for communication log.",
        inputFields: [],
      }),

      // 2. AI extract query
      aiNode(
        "2",
        "AI Extract Status Query",
        280,
        "You are a project status assistant. Parse the stakeholder query to understand what project, timeframe, and specific metrics they are asking about. Identify the requester and their role. Respond JSON: {requester_name: string, requester_role: string, project_key: string, query_topic: string, timeframe: string, specific_metrics: string, response_channel: string, needs_decision: string}",
        "Raw webhook payload: {webhook_payload}. Extract the status query details regardless of format.",
        [
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_role", as: "requester_role", type: "string" },
          { field: "project_key", as: "project_key", type: "string" },
          { field: "query_topic", as: "query_topic", type: "string" },
          { field: "timeframe", as: "query_timeframe", type: "string" },
          { field: "specific_metrics", as: "specific_metrics", type: "string" },
          { field: "response_channel", as: "response_channel", type: "string" },
          { field: "needs_decision", as: "needs_decision", type: "string" },
        ],
      ),

      // 3. Multi-source data pull Jira API
      n("3", "hitApi", "Fetch Project Data from Jira", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/search?jql=project={project_key}+AND+updated>=-14d&maxResults=50&fields=summary,status,assignee,priority",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "total", customName: "total_issues", type: "number" },
            { name: "issues", customName: "jira_issues", type: "string" },
          ],
        },
      }),

      // 4. AI response generator
      aiNode(
        "4",
        "AI Generate Stakeholder Response",
        760,
        "You are a senior project manager generating stakeholder status updates. Tailor the response to the stakeholder role (executives get high-level summaries, managers get detailed metrics, team members get task-level detail). Be concise, data-driven, and proactive about risks. Respond JSON: {status_summary: string, key_metrics: string, risks_and_blockers: string, next_milestones: string, decision_needed: string, decision_context: string, confidence_level: string}",
        "Requester: {requester_name} (Role: {requester_role}). Topic: {query_topic}. Timeframe: {query_timeframe}. Specific metrics requested: {specific_metrics}. Project {project_key} has {total_issues} recently updated issues. Generate a tailored status response.",
        [
          { field: "status_summary", as: "status_summary", type: "string" },
          { field: "key_metrics", as: "key_metrics", type: "string" },
          {
            field: "risks_and_blockers",
            as: "risks_and_blockers",
            type: "string",
          },
          { field: "next_milestones", as: "next_milestones", type: "string" },
          { field: "decision_needed", as: "decision_needed", type: "string" },
          { field: "decision_context", as: "decision_context", type: "string" },
          { field: "confidence_level", as: "confidence_level", type: "string" },
        ],
      ),

      // 5. Proactive update Slack
      slackNode(
        "5",
        "Post Status Update to Slack",
        1000,
        "Status Update for *{project_key}* (requested by {requester_name})\\n\\n{status_summary}\\n\\nKey Metrics: {key_metrics}\\nRisks: {risks_and_blockers}\\nNext Milestones: {next_milestones}\\nConfidence: {confidence_level}",
      ),

      // 6. Decision request handler condition
      n("6", "condition", "Decision Needed from Stakeholder?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "decision_needed", operator: "ne", value: "none" },
          ],
        },
      }),

      // 7. Communication log Sheets
      sheetsNode(
        "7",
        "Log Communication to Sheets",
        1480,
        "StakeholderComms",
        '[["{requester_name}","{requester_role}","{project_key}","{query_topic}","{status_summary}","{decision_needed}","{confidence_level}","' +
          new Date().toISOString() +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1434. STRIPE TO SLACK PAYMENT ALERTS (6 nodes)
  // ===================================================================

  "Stripe to Slack Payment Alerts": {
    summary:
      "Receives Stripe payment events via webhook, extracts payment data with AI, enriches customer info from Stripe API, classifies the payment with runCode, generates a celebration message with AI, and posts to Slack.",
    nodes: [
      trigger("Webhook: Stripe payment event", "webhook_app", {
        source: "stripe",
        event: "payment_intent.succeeded",
        howItStarts:
          "Fires when a Stripe payment succeeds — including subscriptions, one-time purchases, invoices, and manual charges across all payment methods",
        setup:
          "Configure Stripe webhook endpoint for payment_intent.succeeded events. Set OpenAI API key for message generation. Provide Stripe secret key for customer enrichment. Set Slack webhook URL for notifications.",
        inputFields: [],
      }),

      // 2. AI extract payment data
      aiNode(
        "2",
        "AI Extract Payment Data",
        280,
        "You are a payment data parser. Extract the payment amount, currency, customer ID, customer email, payment method, description, and metadata from the raw Stripe webhook payload regardless of nesting. Respond JSON: {amount_cents: number, currency: string, customer_id: string, customer_email: string, payment_method: string, description: string, invoice_id: string, is_subscription: string, metadata: string}",
        "Raw webhook payload: {webhook_payload}. Extract all Stripe payment details regardless of the payload structure.",
        [
          { field: "amount_cents", as: "amount_cents", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          { field: "customer_id", as: "customer_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "payment_method", as: "payment_method", type: "string" },
          { field: "description", as: "payment_description", type: "string" },
          { field: "invoice_id", as: "invoice_id", type: "string" },
          { field: "is_subscription", as: "is_subscription", type: "string" },
          { field: "metadata", as: "payment_metadata", type: "string" },
        ],
      ),

      // 3. Customer data enrichment Stripe API
      n("3", "hitApi", "Enrich Customer Data from Stripe", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/customers/{customer_id}",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "customer_name", type: "string" },
            {
              name: "email",
              customName: "customer_email_verified",
              type: "string",
            },
            {
              name: "metadata.company",
              customName: "customer_company",
              type: "string",
            },
            {
              name: "metadata.plan",
              customName: "customer_plan",
              type: "string",
            },
            { name: "created", customName: "customer_since", type: "number" },
          ],
        },
      }),

      // 4. Payment classifier runCode
      n("4", "runCode", "Classify Payment Type and Tier", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify payment by amount tier and type
const amountCents = parseInt(vars.amount_cents || "0", 10);
const amountDollars = (amountCents / 100).toFixed(2);
const currency = (vars.currency || "usd").toUpperCase();
const isSub = vars.is_subscription === "true" || vars.is_subscription === "yes";
const tier = amountCents >= 100000 ? "enterprise" : amountCents >= 10000 ? "growth" : amountCents >= 1000 ? "starter" : "micro";
const emoji = tier === "enterprise" ? ":rocket:" : tier === "growth" ? ":chart_with_upwards_trend:" : tier === "starter" ? ":tada:" : ":zap:";
const paymentType = isSub ? "subscription" : "one-time";
const alertChannel = tier === "enterprise" ? "#enterprise-wins" : "#revenue-alerts";
return {
  amount_formatted: currency + " " + amountDollars,
  payment_tier: tier,
  tier_emoji: emoji,
  payment_type: paymentType,
  alert_channel: alertChannel,
  celebration_worthy: amountCents >= 10000 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "amount_formatted", type: "string" },
            { name: "payment_tier", type: "string" },
            { name: "tier_emoji", type: "string" },
            { name: "payment_type", type: "string" },
            { name: "alert_channel", type: "string" },
            { name: "celebration_worthy", type: "string" },
          ],
        },
      }),

      // 5. AI celebration message
      aiNode(
        "5",
        "AI Generate Payment Alert Message",
        1000,
        "You are a revenue celebration bot. Generate a short, enthusiastic Slack message announcing a payment. Include the amount, customer name, payment type, and a brief congratulatory note. Keep it under 200 characters for impact. Respond JSON: {slack_message: string, highlight: string}",
        "Payment: {amount_formatted} from {customer_name} ({customer_company}). Plan: {customer_plan}. Type: {payment_type}. Tier: {payment_tier}. Generate an engaging Slack alert.",
        [
          { field: "slack_message", as: "celebration_message", type: "string" },
          { field: "highlight", as: "payment_highlight", type: "string" },
        ],
      ),

      // 6. Slack channel post
      slackNode(
        "6",
        "Post Payment Alert to Slack",
        1240,
        "{tier_emoji} *New {payment_type} payment!* {tier_emoji}\\n{celebration_message}\\n*Amount:* {amount_formatted} | *Customer:* {customer_name} ({customer_company})\\n*Plan:* {customer_plan} | *Tier:* {payment_tier}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 1435. SUBSCRIPTION BOX MANAGER (8 nodes)
  // ===================================================================

  "Subscription Box Manager": {
    summary:
      "Runs monthly on schedule, fetches subscriber data from Shopify, generates AI-powered box contents, checks inventory availability via Shopify API, sends customer preview emails, notifies fulfillment team via Slack, generates packing lists with runCode, and schedules feedback collection with a delay node.",
    nodes: [
      trigger("Schedule: Monthly subscription box prep", "schedule", {
        schedule: "1st of every month at 9:00 AM",
        howItStarts:
          "Runs on the first of each month to prepare subscription boxes — pulling subscriber preferences, generating personalized box contents, checking inventory, and coordinating fulfillment",
        setup:
          "Configure Shopify API credentials for subscriber and inventory data. Set OpenAI API key for content personalization. Configure email for customer previews. Set Slack webhook for fulfillment team notifications.",
        inputFields: [],
      }),

      // 2. Subscriber data fetch Shopify API
      n("2", "hitApi", "Fetch Active Subscribers from Shopify", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/customers.json?tag=active-subscriber&limit=250",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "customers",
              customName: "subscriber_list",
              type: "string",
            },
            {
              name: "customers.length",
              customName: "subscriber_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI box contents generator
      aiNode(
        "3",
        "AI Generate Personalized Box Contents",
        520,
        "You are a subscription box curator. Based on subscriber count and seasonal trends, generate a curated box theme and product selection. Consider variety, value perception, and seasonal relevance. Respond JSON: {box_theme: string, product_list: string, product_count: number, estimated_value: number, hero_product: string, seasonal_tie: string, personalization_notes: string}",
        "Subscriber count: {subscriber_count}. Current month: " +
          new Date().toLocaleString("en-US", { month: "long" }) +
          ". Generate a compelling subscription box with 4-6 products. Theme should feel premium and seasonal.",
        [
          { field: "box_theme", as: "box_theme", type: "string" },
          { field: "product_list", as: "product_list", type: "string" },
          { field: "product_count", as: "product_count", type: "number" },
          { field: "estimated_value", as: "estimated_value", type: "number" },
          { field: "hero_product", as: "hero_product", type: "string" },
          { field: "seasonal_tie", as: "seasonal_tie", type: "string" },
          {
            field: "personalization_notes",
            as: "personalization_notes",
            type: "string",
          },
        ],
      ),

      // 4. Inventory availability check Shopify API
      n("4", "hitApi", "Check Inventory Availability in Shopify", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/inventory_levels.json?location_ids={warehouse_location_id}&limit=250",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "inventory_levels",
              customName: "inventory_data",
              type: "string",
            },
            {
              name: "inventory_levels.length",
              customName: "inventory_items_checked",
              type: "number",
            },
          ],
        },
      }),

      // 5. Customer preview email
      emailNode(
        "5",
        "Send Box Preview Email to Subscribers",
        1000,
        "{subscriber_notification_email}",
        "Your {box_theme} Box is Coming! Sneak Peek Inside",
        "<h2>Your Monthly Box: {box_theme}</h2><p>We have curated something special for you this month!</p><h3>This Month's Hero Product</h3><p>{hero_product}</p><h3>What's Inside ({product_count} items, ${estimated_value}+ value)</h3><p>{product_list}</p><p>{seasonal_tie}</p><p>{personalization_notes}</p><p>Your box ships within 5 business days. Stay tuned!</p>",
      ),

      // 6. Fulfillment notification Slack
      slackNode(
        "6",
        "Notify Fulfillment Team via Slack",
        1240,
        "Monthly Subscription Box Prep\\n*Theme:* {box_theme}\\n*Subscribers:* {subscriber_count}\\n*Products:* {product_count} items per box\\n*Hero Product:* {hero_product}\\n*Inventory Items Checked:* {inventory_items_checked}\\n\\nPlease begin packing prep. Packing lists incoming.",
      ),

      // 7. Packing list generator runCode
      n("7", "runCode", "Generate Packing List Summary", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate packing list from box contents and subscriber count
const subscribers = parseInt(vars.subscriber_count || "0", 10);
const productCount = parseInt(vars.product_count || "0", 10);
const products = (vars.product_list || "").split(",").map(p => p.trim()).filter(Boolean);
const totalItems = subscribers * productCount;
const packingLines = products.map((p, i) => "Item " + (i + 1) + ": " + p + " x " + subscribers + " units");
const packingListText = packingLines.join("\\n");
const estimatedPackingHours = Math.ceil(subscribers * 0.1);
const shippingDate = new Date(Date.now() + 5 * 86400000).toISOString().split("T")[0];
return {
  total_items_needed: totalItems,
  packing_list: packingListText,
  estimated_packing_hours: estimatedPackingHours,
  target_shipping_date: shippingDate,
  boxes_to_pack: subscribers
};`,
          outputVariables: [
            { name: "total_items_needed", type: "number" },
            { name: "packing_list", type: "string" },
            { name: "estimated_packing_hours", type: "number" },
            { name: "target_shipping_date", type: "string" },
            { name: "boxes_to_pack", type: "number" },
          ],
        },
      }),

      // 8. Feedback collection scheduler delay
      n("8", "delayAction", "Schedule Feedback Collection (7 days)", 1720, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 7,
          delayUnit: "days",
          description:
            "Wait 7 days after shipping to send feedback survey to subscribers",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1436. SUBSCRIPTION RENEWAL REMINDER (7 nodes)
  // ===================================================================

  "Subscription Renewal Reminder": {
    summary:
      "Runs daily on schedule, scans renewal windows from Stripe, fetches subscriber data from HubSpot, personalizes reminders with AI, validates payment methods with runCode, delivers reminder emails, and routes escalations via condition for failed payment methods.",
    nodes: [
      trigger("Schedule: Daily renewal reminder scan", "schedule", {
        schedule: "Every day at 10:00 AM",
        howItStarts:
          "Runs every morning to identify subscriptions within the renewal window (7, 3, and 1 day before expiry), check payment method validity, and send personalized reminders",
        setup:
          "Configure Stripe API key for subscription scanning. Set HubSpot token for subscriber data. Set OpenAI API key for personalized reminders. Configure email delivery for reminders.",
        inputFields: [],
      }),

      // 2. Renewal window scanner Stripe API
      n("2", "hitApi", "Scan Upcoming Renewals from Stripe", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/subscriptions?status=active&current_period_end[lt]={renewal_window_end}&current_period_end[gt]={renewal_window_start}&limit=100",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [],
          variables: [
            {
              name: "data",
              customName: "renewal_subscriptions",
              type: "string",
            },
            {
              name: "data.length",
              customName: "renewal_count",
              type: "number",
            },
            {
              name: "data[0].customer",
              customName: "first_customer_id",
              type: "string",
            },
            {
              name: "data[0].current_period_end",
              customName: "first_renewal_date",
              type: "number",
            },
            {
              name: "data[0].plan.amount",
              customName: "first_plan_amount",
              type: "number",
            },
            {
              name: "data[0].plan.nickname",
              customName: "first_plan_name",
              type: "string",
            },
          ],
        },
      }),

      // 3. Subscriber data fetch HubSpot
      hubspotGetNode(
        "3",
        "Fetch Subscriber Details from HubSpot",
        520,
        "crm/v3/objects/contacts/{first_customer_id}?properties=firstname,lastname,email,phone,company",
        [
          {
            name: "properties.firstname",
            customName: "subscriber_first_name",
            type: "string",
          },
          {
            name: "properties.lastname",
            customName: "subscriber_last_name",
            type: "string",
          },
          {
            name: "properties.email",
            customName: "subscriber_email",
            type: "string",
          },
          {
            name: "properties.company",
            customName: "subscriber_company",
            type: "string",
          },
        ],
      ),

      // 4. AI reminder personalizer
      aiNode(
        "4",
        "AI Personalize Renewal Reminder",
        760,
        "You are a customer retention specialist. Write a warm, personalized renewal reminder email. Emphasize value received, mention the specific plan, and include a clear call to action. Be friendly but create appropriate urgency based on days until renewal. Respond JSON: {subject_line: string, email_body: string, urgency_note: string, value_proposition: string}",
        "Subscriber: {subscriber_first_name} {subscriber_last_name} at {subscriber_company}. Plan: {first_plan_name}. Renewal date: {first_renewal_date}. Amount: {first_plan_amount} cents. Renewals pending: {renewal_count}. Write a compelling renewal reminder.",
        [
          { field: "subject_line", as: "reminder_subject", type: "string" },
          { field: "email_body", as: "reminder_body", type: "string" },
          { field: "urgency_note", as: "urgency_note", type: "string" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
        ],
      ),

      // 5. Payment method validator runCode
      n("5", "runCode", "Validate Payment Method Status", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate payment method and calculate renewal details
const amountCents = parseInt(vars.first_plan_amount || "0", 10);
const amountFormatted = "$" + (amountCents / 100).toFixed(2);
const renewalTimestamp = parseInt(vars.first_renewal_date || "0", 10);
const renewalDate = renewalTimestamp > 0 ? new Date(renewalTimestamp * 1000) : new Date();
const daysUntilRenewal = Math.max(0, Math.floor((renewalDate - new Date()) / (1000 * 60 * 60 * 24)));
const renewalDateStr = renewalDate.toISOString().split("T")[0];
const paymentValid = amountCents > 0 ? "yes" : "no";
const needsEscalation = daysUntilRenewal <= 1 || paymentValid === "no" ? "yes" : "no";
return {
  amount_display: amountFormatted,
  days_until_renewal: daysUntilRenewal,
  renewal_date_display: renewalDateStr,
  payment_valid: paymentValid,
  needs_escalation: needsEscalation,
  renewal_urgency: daysUntilRenewal <= 1 ? "critical" : daysUntilRenewal <= 3 ? "high" : "normal"
};`,
          outputVariables: [
            { name: "amount_display", type: "string" },
            { name: "days_until_renewal", type: "number" },
            { name: "renewal_date_display", type: "string" },
            { name: "payment_valid", type: "string" },
            { name: "needs_escalation", type: "string" },
            { name: "renewal_urgency", type: "string" },
          ],
        },
      }),

      // 6. Reminder delivery email
      emailNode(
        "6",
        "Send Renewal Reminder Email",
        1240,
        "{subscriber_email}",
        "{reminder_subject}",
        "<p>Hi {subscriber_first_name},</p><p>{reminder_body}</p><p><strong>Plan:</strong> {first_plan_name} — {amount_display}/period</p><p><strong>Renewal Date:</strong> {renewal_date_display} ({days_until_renewal} days away)</p><p>{urgency_note}</p><p>{value_proposition}</p>",
      ),

      // 7. Escalation path condition
      n("7", "condition", "Needs Payment Escalation?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "needs_escalation", operator: "eq", value: "yes" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1437. SUPPLIER COMMUNICATION BOT (7 nodes)
  // ===================================================================

  "Supplier Communication Bot": {
    summary:
      "Receives supplier messages via webhook, extracts message data with AI, classifies intent with a second AI pass, fetches supplier context from HubSpot, generates an appropriate response with AI, routes through an approval gate via condition, and delivers the response by email.",
    nodes: [
      trigger("Webhook: Supplier message received", "webhook_app", {
        source: "email_system",
        event: "supplier.message_received",
        howItStarts:
          "Fires when a supplier sends a message via email, portal, or API — including order inquiries, delivery updates, invoice submissions, complaint escalations, and partnership proposals",
        setup:
          "Configure webhook from email parser or supplier portal. Set OpenAI API key for intent classification and response generation. Provide HubSpot token for supplier context. Configure email for response delivery.",
        inputFields: [],
      }),

      // 2. AI extract message data
      aiNode(
        "2",
        "AI Extract Supplier Message Data",
        280,
        "You are a supplier communication parser. Extract the sender details, subject, message content, any referenced order or invoice numbers, and urgency from the raw webhook payload. Respond JSON: {supplier_name: string, supplier_email: string, subject: string, message_content: string, reference_numbers: string, attachment_count: number, language: string, urgency: string}",
        "Raw webhook payload: {webhook_payload}. Extract all supplier message details regardless of format.",
        [
          { field: "supplier_name", as: "supplier_name", type: "string" },
          { field: "supplier_email", as: "supplier_email", type: "string" },
          { field: "subject", as: "message_subject", type: "string" },
          { field: "message_content", as: "message_content", type: "string" },
          {
            field: "reference_numbers",
            as: "reference_numbers",
            type: "string",
          },
          { field: "attachment_count", as: "attachment_count", type: "number" },
          { field: "language", as: "message_language", type: "string" },
          { field: "urgency", as: "message_urgency", type: "string" },
        ],
      ),

      // 3. AI intent classifier
      aiNode(
        "3",
        "AI Classify Supplier Intent",
        520,
        "You are a business communication analyst. Classify the supplier message into one of these intents: order_inquiry, delivery_update, invoice_submission, pricing_request, complaint, partnership_proposal, general_inquiry, escalation. Also determine the appropriate response tone and whether human approval is needed. Respond JSON: {intent: string, sub_intent: string, sentiment: string, response_tone: string, needs_approval: string, department: string, priority: string}",
        "Supplier: {supplier_name}. Subject: {message_subject}. Content: {message_content}. References: {reference_numbers}. Urgency: {message_urgency}. Classify the intent accurately.",
        [
          { field: "intent", as: "supplier_intent", type: "string" },
          { field: "sub_intent", as: "sub_intent", type: "string" },
          { field: "sentiment", as: "supplier_sentiment", type: "string" },
          { field: "response_tone", as: "response_tone", type: "string" },
          { field: "needs_approval", as: "needs_approval", type: "string" },
          { field: "department", as: "routing_department", type: "string" },
          { field: "priority", as: "message_priority", type: "string" },
        ],
      ),

      // 4. Supplier context fetch HubSpot
      hubspotGetNode(
        "4",
        "Fetch Supplier Context from HubSpot",
        760,
        "crm/v3/objects/companies/search?query={supplier_name}",
        [
          {
            name: "results[0].properties.name",
            customName: "hs_supplier_name",
            type: "string",
          },
          {
            name: "results[0].properties.supplier_tier",
            customName: "supplier_tier",
            type: "string",
          },
          {
            name: "results[0].properties.account_manager",
            customName: "account_manager",
            type: "string",
          },
          {
            name: "results[0].properties.contract_status",
            customName: "contract_status",
            type: "string",
          },
        ],
      ),

      // 5. AI response generator
      aiNode(
        "5",
        "AI Generate Supplier Response",
        1000,
        "You are a professional supplier relations manager. Generate an appropriate response based on the message intent, supplier tier, and sentiment. Be professional, address their specific concerns, and reference any order/invoice numbers. Match the determined response tone. Respond JSON: {response_subject: string, response_body: string, internal_note: string, follow_up_action: string}",
        "Supplier: {supplier_name} (Tier: {supplier_tier}). Intent: {supplier_intent}. Sentiment: {supplier_sentiment}. Original subject: {message_subject}. Content: {message_content}. References: {reference_numbers}. Tone: {response_tone}. Account Manager: {account_manager}. Contract: {contract_status}. Generate a professional response.",
        [
          { field: "response_subject", as: "response_subject", type: "string" },
          { field: "response_body", as: "response_body", type: "string" },
          { field: "internal_note", as: "internal_note", type: "string" },
          { field: "follow_up_action", as: "follow_up_action", type: "string" },
        ],
      ),

      // 6. Approval gate condition
      n("6", "condition", "Human Approval Required?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "needs_approval", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 7. Response delivery email
      emailNode(
        "7",
        "Send Response to Supplier",
        1480,
        "{supplier_email}",
        "Re: {response_subject}",
        "<p>Dear {supplier_name},</p><p>{response_body}</p><p>Best regards,<br>Procurement Team</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1438. SUPPLIER PERFORMANCE TRACKER (8 nodes)
  // ===================================================================

  "Supplier Performance Tracker": {
    summary:
      "Runs monthly on schedule, fetches delivery metrics from API, fetches quality metrics from API, calculates financial metrics with runCode, scores performance with AI, applies threshold alerts via condition, generates scorecards with runCode, and distributes scorecards by email.",
    nodes: [
      trigger("Schedule: Monthly supplier performance review", "schedule", {
        schedule: "1st of every month at 7:00 AM",
        howItStarts:
          "Runs on the first of each month to compile supplier delivery, quality, and financial metrics into performance scorecards with automated threshold alerts",
        setup:
          "Configure ERP/procurement API endpoints for delivery and quality metrics. Set OpenAI API key for AI scoring. Configure email for scorecard distribution. Provide threshold values for alerting.",
        inputFields: [],
      }),

      // 2. Delivery metrics fetch API
      n("2", "hitApi", "Fetch Delivery Metrics from Procurement API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.procurement-system.com/v1/suppliers/metrics/delivery?period=last_month",
          headers: [
            { key: "Authorization", value: "Bearer {procurement_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data.on_time_rate",
              customName: "on_time_delivery_rate",
              type: "number",
            },
            {
              name: "data.total_orders",
              customName: "total_orders",
              type: "number",
            },
            {
              name: "data.late_orders",
              customName: "late_orders",
              type: "number",
            },
            {
              name: "data.avg_lead_time_days",
              customName: "avg_lead_time",
              type: "number",
            },
            {
              name: "data.suppliers_evaluated",
              customName: "suppliers_evaluated",
              type: "number",
            },
          ],
        },
      }),

      // 3. Quality metrics fetch API
      n("3", "hitApi", "Fetch Quality Metrics from QA API", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.procurement-system.com/v1/suppliers/metrics/quality?period=last_month",
          headers: [
            { key: "Authorization", value: "Bearer {procurement_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data.defect_rate",
              customName: "defect_rate",
              type: "number",
            },
            {
              name: "data.return_rate",
              customName: "return_rate",
              type: "number",
            },
            {
              name: "data.compliance_score",
              customName: "compliance_score",
              type: "number",
            },
            {
              name: "data.inspection_pass_rate",
              customName: "inspection_pass_rate",
              type: "number",
            },
          ],
        },
      }),

      // 4. Financial metrics calculator runCode
      n("4", "runCode", "Calculate Financial Performance Metrics", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate financial performance metrics from raw data
const totalOrders = parseInt(vars.total_orders || "0", 10);
const lateOrders = parseInt(vars.late_orders || "0", 10);
const avgLeadTime = parseFloat(vars.avg_lead_time || "0");
const defectRate = parseFloat(vars.defect_rate || "0");
const returnRate = parseFloat(vars.return_rate || "0");
const latePenaltyCost = lateOrders * 150;
const defectCost = Math.round(totalOrders * (defectRate / 100) * 200);
const totalQualityCost = latePenaltyCost + defectCost;
const costPerOrder = totalOrders > 0 ? Math.round(totalQualityCost / totalOrders) : 0;
const savingsOpportunity = Math.round(totalQualityCost * 0.3);
return {
  late_penalty_cost: latePenaltyCost,
  defect_cost: defectCost,
  total_quality_cost: totalQualityCost,
  cost_per_order: costPerOrder,
  savings_opportunity: savingsOpportunity,
  financial_summary: "Late penalties: $" + latePenaltyCost + " | Defect costs: $" + defectCost + " | Total: $" + totalQualityCost
};`,
          outputVariables: [
            { name: "late_penalty_cost", type: "number" },
            { name: "defect_cost", type: "number" },
            { name: "total_quality_cost", type: "number" },
            { name: "cost_per_order", type: "number" },
            { name: "savings_opportunity", type: "number" },
            { name: "financial_summary", type: "string" },
          ],
        },
      }),

      // 5. AI performance scorer
      aiNode(
        "5",
        "AI Score Supplier Performance",
        1000,
        "You are a procurement analytics expert. Analyze the supplier delivery, quality, and financial metrics to generate an overall performance score (0-100) and detailed assessment. Identify top performers, at-risk suppliers, and specific improvement areas. Respond JSON: {overall_score: number, grade: string, top_performers: string, at_risk_suppliers: string, improvement_areas: string, executive_summary: string, action_required: string, threshold_breached: string}",
        "Delivery: {on_time_delivery_rate}% on-time, {late_orders} late of {total_orders} total, {avg_lead_time} day avg lead time. Quality: {defect_rate}% defect rate, {return_rate}% return rate, {compliance_score} compliance, {inspection_pass_rate}% pass rate. Financial: {financial_summary}. Suppliers evaluated: {suppliers_evaluated}. Score comprehensively.",
        [
          { field: "overall_score", as: "overall_score", type: "number" },
          { field: "grade", as: "performance_grade", type: "string" },
          { field: "top_performers", as: "top_performers", type: "string" },
          {
            field: "at_risk_suppliers",
            as: "at_risk_suppliers",
            type: "string",
          },
          {
            field: "improvement_areas",
            as: "improvement_areas",
            type: "string",
          },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "action_required", as: "action_required", type: "string" },
          {
            field: "threshold_breached",
            as: "threshold_breached",
            type: "string",
          },
        ],
      ),

      // 6. Threshold alert condition
      n("6", "condition", "Performance Threshold Breached?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "threshold_breached", operator: "ne", value: "none" },
          ],
        },
      }),

      // 7. Supplier scorecard generator runCode
      n("7", "runCode", "Generate Supplier Scorecard Report", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build supplier scorecard HTML report
const html = "<h1>Monthly Supplier Performance Scorecard</h1>" +
  "<p><strong>Period:</strong> " + new Date().toLocaleString("en-US", { month: "long", year: "numeric" }) + "</p>" +
  "<p><strong>Suppliers Evaluated:</strong> " + (vars.suppliers_evaluated || "0") + "</p>" +
  "<p><strong>Overall Score:</strong> " + (vars.overall_score || "0") + "/100 — Grade: " + (vars.performance_grade || "N/A") + "</p>" +
  "<hr><h2>Delivery Performance</h2>" +
  "<ul><li>On-Time Rate: " + (vars.on_time_delivery_rate || "0") + "%</li>" +
  "<li>Late Orders: " + (vars.late_orders || "0") + " of " + (vars.total_orders || "0") + "</li>" +
  "<li>Avg Lead Time: " + (vars.avg_lead_time || "0") + " days</li></ul>" +
  "<h2>Quality Metrics</h2>" +
  "<ul><li>Defect Rate: " + (vars.defect_rate || "0") + "%</li>" +
  "<li>Return Rate: " + (vars.return_rate || "0") + "%</li>" +
  "<li>Compliance Score: " + (vars.compliance_score || "0") + "</li>" +
  "<li>Inspection Pass Rate: " + (vars.inspection_pass_rate || "0") + "%</li></ul>" +
  "<h2>Financial Impact</h2><p>" + (vars.financial_summary || "N/A") + "</p>" +
  "<p>Savings Opportunity: $" + (vars.savings_opportunity || "0") + "</p>" +
  "<h2>Executive Summary</h2><p>" + (vars.executive_summary || "N/A") + "</p>" +
  "<h2>Action Required</h2><p>" + (vars.action_required || "None") + "</p>" +
  "<h2>Top Performers</h2><p>" + (vars.top_performers || "N/A") + "</p>" +
  "<h2>At-Risk Suppliers</h2><p>" + (vars.at_risk_suppliers || "None") + "</p>";
return { scorecard_html: html, report_date: new Date().toISOString().split("T")[0] };`,
          outputVariables: [
            { name: "scorecard_html", type: "string" },
            { name: "report_date", type: "string" },
          ],
        },
      }),

      // 8. Scorecard distribution email
      emailNode(
        "8",
        "Email Scorecard to Procurement Team",
        1720,
        "{procurement_team_email}",
        "Monthly Supplier Scorecard — Score: {overall_score}/100 ({performance_grade}) — {report_date}",
        "{scorecard_html}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1439. TASK ASSIGNMENT WEBHOOK (7 nodes)
  // ===================================================================

  "Task Assignment Webhook": {
    summary:
      "Receives task assignment events via webhook, extracts task data with AI, enriches the task with AI-generated context, resolves the assignee with runCode, creates the task in Asana, notifies the assignee via Slack, and logs the assignment to Sheets.",
    nodes: [
      trigger("Webhook: Task assignment received", "webhook_app", {
        source: "project_management",
        event: "task.assigned",
        howItStarts:
          "Fires when a task is assigned through any channel — PM tool, email forward, Slack command, or API call — including the task description, assignee, priority, and deadline",
        setup:
          "Configure webhook from your task assignment source. Set OpenAI API key for task enrichment. Provide Asana token and project GID. Set Slack webhook URL. Configure Google Sheets for tracking.",
        inputFields: [],
      }),

      // 2. AI extract task data
      aiNode(
        "2",
        "AI Extract Task Assignment Data",
        280,
        "You are a task management assistant. Extract the task title, description, assignee name or email, priority level, due date, project or category, and any dependencies from the raw webhook payload. Respond JSON: {task_title: string, task_description: string, assignee_identifier: string, priority: string, due_date: string, project_name: string, dependencies: string, estimated_hours: string, requester: string}",
        "Raw webhook payload: {webhook_payload}. Extract all task assignment details regardless of field naming.",
        [
          { field: "task_title", as: "task_title", type: "string" },
          { field: "task_description", as: "task_description", type: "string" },
          {
            field: "assignee_identifier",
            as: "assignee_identifier",
            type: "string",
          },
          { field: "priority", as: "task_priority", type: "string" },
          { field: "due_date", as: "task_due_date", type: "string" },
          { field: "project_name", as: "project_name", type: "string" },
          { field: "dependencies", as: "task_dependencies", type: "string" },
          { field: "estimated_hours", as: "estimated_hours", type: "string" },
          { field: "requester", as: "task_requester", type: "string" },
        ],
      ),

      // 3. AI task enricher
      aiNode(
        "3",
        "AI Enrich Task with Context",
        520,
        "You are a project management expert. Analyze the task and add helpful context: break it into subtasks if complex, suggest acceptance criteria, identify potential blockers, and estimate effort if not provided. Respond JSON: {subtasks: string, acceptance_criteria: string, potential_blockers: string, effort_estimate: string, category_tag: string, recommended_approach: string}",
        "Task: {task_title}. Description: {task_description}. Priority: {task_priority}. Due: {task_due_date}. Dependencies: {task_dependencies}. Estimated hours: {estimated_hours}. Enrich with actionable context.",
        [
          { field: "subtasks", as: "subtasks", type: "string" },
          {
            field: "acceptance_criteria",
            as: "acceptance_criteria",
            type: "string",
          },
          {
            field: "potential_blockers",
            as: "potential_blockers",
            type: "string",
          },
          { field: "effort_estimate", as: "effort_estimate", type: "string" },
          { field: "category_tag", as: "category_tag", type: "string" },
          {
            field: "recommended_approach",
            as: "recommended_approach",
            type: "string",
          },
        ],
      ),

      // 4. Assignee resolver runCode
      n("4", "runCode", "Resolve Assignee Details", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Resolve assignee from identifier (email or name)
const identifier = (vars.assignee_identifier || "").trim();
const isEmail = identifier.includes("@");
const assigneeEmail = isEmail ? identifier : identifier.replace(/\\s+/g, ".").toLowerCase() + "@company.com";
const assigneeName = isEmail ? identifier.split("@")[0].replace(/\\./g, " ") : identifier;
const priority = (vars.task_priority || "medium").toLowerCase();
const priorityTag = priority === "critical" ? "[CRITICAL]" : priority === "high" ? "[HIGH]" : priority === "medium" ? "[MED]" : "[LOW]";
const dueDate = vars.task_due_date || new Date(Date.now() + 7 * 86400000).toISOString().split("T")[0];
return {
  assignee_email: assigneeEmail,
  assignee_name: assigneeName,
  priority_tag: priorityTag,
  resolved_due_date: dueDate,
  assignment_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "assignee_email", type: "string" },
            { name: "assignee_name", type: "string" },
            { name: "priority_tag", type: "string" },
            { name: "resolved_due_date", type: "string" },
            { name: "assignment_timestamp", type: "string" },
          ],
        },
      }),

      // 5. Task creator Asana API
      n("5", "hitApi", "Create Task in Asana", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"{priority_tag} {task_title}","notes":"Description: {task_description}\\n\\nSubtasks: {subtasks}\\n\\nAcceptance Criteria: {acceptance_criteria}\\n\\nPotential Blockers: {potential_blockers}\\n\\nRecommended Approach: {recommended_approach}\\n\\nEffort Estimate: {effort_estimate}\\nDependencies: {task_dependencies}\\nRequested by: {task_requester}","projects":["{asana_project_gid}"],"due_on":"{resolved_due_date}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_gid", type: "string" },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
          ],
        },
      }),

      // 6. Assignee notification Slack
      slackNode(
        "6",
        "Notify Assignee via Slack",
        1240,
        "{priority_tag} New task assigned to *{assignee_name}*\\n*{task_title}*\\nPriority: {task_priority} | Due: {resolved_due_date}\\nDescription: {task_description}\\nSubtasks: {subtasks}\\nAsana: {asana_task_url}\\nAssigned by: {task_requester}",
      ),

      // 7. Assignment tracker Sheets
      sheetsNode(
        "7",
        "Log Assignment to Tracker",
        1480,
        "TaskAssignments",
        '[["{assignment_timestamp}","{task_title}","{assignee_name}","{assignee_email}","{task_priority}","{resolved_due_date}","{project_name}","{category_tag}","{effort_estimate}","{asana_task_gid}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1440. TEAM AVAILABILITY CALENDAR (7 nodes)
  // ===================================================================

  "Team Availability Calendar": {
    summary:
      "Runs daily on schedule, aggregates calendars from Google Calendar, integrates PTO/leave data from HR API, builds an availability matrix with runCode, detects conflicts via condition, publishes the dashboard to Sheets, and alerts the team via Slack.",
    nodes: [
      trigger("Schedule: Daily team availability sync", "schedule", {
        schedule: "Every day at 7:30 AM",
        howItStarts:
          "Runs every morning to aggregate team calendars, PTO schedules, and leave data into a unified availability dashboard with conflict detection",
        setup:
          "Configure Google Calendar API credentials with read access to team calendars. Provide HR system API for PTO/leave data. Set Google Sheets spreadsheet ID for dashboard. Set Slack webhook for alerts.",
        inputFields: [],
      }),

      // 2. Multi-calendar aggregator Google Calendar API
      n("2", "hitApi", "Aggregate Team Calendars from Google", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin={today_start}&timeMax={today_end}&singleEvents=true&orderBy=startTime&maxResults=100",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "items", customName: "calendar_events", type: "string" },
            { name: "items.length", customName: "event_count", type: "number" },
            { name: "summary", customName: "calendar_name", type: "string" },
          ],
        },
      }),

      // 3. PTO leave integration API
      n("3", "hitApi", "Fetch PTO and Leave Data from HR System", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hr-system.com/v1/time-off?date={today_date}&status=approved",
          headers: [
            { key: "Authorization", value: "Bearer {hr_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "pto_records", type: "string" },
            { name: "data.length", customName: "pto_count", type: "number" },
          ],
        },
      }),

      // 4. Availability matrix builder runCode
      n("4", "runCode", "Build Team Availability Matrix", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build availability matrix from calendar events and PTO data
const events = JSON.parse(vars.calendar_events || "[]");
const ptoRecords = JSON.parse(vars.pto_records || "[]");
const eventCount = parseInt(vars.event_count || "0", 10);
const ptoCount = parseInt(vars.pto_count || "0", 10);
const today = new Date().toISOString().split("T")[0];
const busySlots = events.filter(e => e.status !== "cancelled").length;
const availableSlots = Math.max(0, 16 - busySlots);
const utilizationPct = eventCount > 0 ? Math.round((busySlots / 16) * 100) : 0;
const onPto = ptoRecords.map(r => (r.employee_name || r.email || "Unknown")).join(", ") || "none";
const teamSize = 10;
const availableMembers = Math.max(0, teamSize - ptoCount);
const hasConflicts = busySlots > 12 || ptoCount > Math.floor(teamSize / 2) ? "yes" : "no";
const coverageLevel = availableMembers >= 8 ? "full" : availableMembers >= 5 ? "adequate" : "critical";
return {
  available_slots: availableSlots,
  busy_slots: busySlots,
  utilization_pct: utilizationPct,
  on_pto: onPto,
  pto_count: ptoCount,
  available_members: availableMembers,
  has_conflicts: hasConflicts,
  coverage_level: coverageLevel,
  matrix_date: today,
  summary_text: availableMembers + " of " + teamSize + " available | " + availableSlots + " open calendar slots | " + ptoCount + " on PTO"
};`,
          outputVariables: [
            { name: "available_slots", type: "number" },
            { name: "busy_slots", type: "number" },
            { name: "utilization_pct", type: "number" },
            { name: "on_pto", type: "string" },
            { name: "pto_count", type: "number" },
            { name: "available_members", type: "number" },
            { name: "has_conflicts", type: "string" },
            { name: "coverage_level", type: "string" },
            { name: "matrix_date", type: "string" },
            { name: "summary_text", type: "string" },
          ],
        },
      }),

      // 5. Conflict detector condition
      n("5", "condition", "Coverage Conflicts Detected?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "has_conflicts", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 6. Dashboard publisher Sheets
      sheetsNode(
        "6",
        "Publish Availability Dashboard to Sheets",
        1240,
        "TeamAvailability",
        '[["{matrix_date}","{available_members}","{pto_count}","{on_pto}","{available_slots}","{busy_slots}","{utilization_pct}","{coverage_level}","{summary_text}"]]',
      ),

      // 7. Alert system Slack
      slackNode(
        "7",
        "Post Availability Update to Slack",
        1480,
        "Team Availability — {matrix_date}\\n{summary_text}\\nCoverage: {coverage_level} | Utilization: {utilization_pct}%\\nOn PTO: {on_pto}\\nConflicts: {has_conflicts}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1441. TEAM EVENT PLANNER BOT (8 nodes)
  // ===================================================================

  "Team Event Planner Bot": {
    summary:
      "Receives event requests via webhook, extracts event data with AI, suggests venues and activities with AI, coordinates availability via Google Calendar, calculates budget with runCode, sends voting options via Slack, books the venue via API, and emails logistics details to the team.",
    nodes: [
      trigger("Webhook: Team event request", "webhook_app", {
        source: "slack_command",
        event: "team_event.requested",
        howItStarts:
          "Fires when someone requests a team event via Slack command, form, or direct message — specifying event type, headcount, budget range, preferred dates, and any special requirements",
        setup:
          "Configure webhook from Slack slash command or event request form. Set OpenAI API key. Provide Google Calendar API credentials. Set Slack webhook for voting. Configure venue booking API credentials. Set email for logistics coordination.",
        inputFields: [],
      }),

      // 2. AI extract event data
      aiNode(
        "2",
        "AI Extract Event Request Details",
        280,
        "You are an event planning assistant. Extract the event type, headcount, budget range, preferred dates, location preferences, dietary restrictions, and any special requirements from the raw webhook payload. Respond JSON: {event_type: string, headcount: number, budget_min: number, budget_max: number, preferred_dates: string, location_pref: string, dietary_restrictions: string, special_requirements: string, organizer_email: string, organizer_name: string}",
        "Raw webhook payload: {webhook_payload}. Extract all event request details regardless of field naming.",
        [
          { field: "event_type", as: "event_type", type: "string" },
          { field: "headcount", as: "headcount", type: "number" },
          { field: "budget_min", as: "budget_min", type: "number" },
          { field: "budget_max", as: "budget_max", type: "number" },
          { field: "preferred_dates", as: "preferred_dates", type: "string" },
          { field: "location_pref", as: "location_pref", type: "string" },
          {
            field: "dietary_restrictions",
            as: "dietary_restrictions",
            type: "string",
          },
          {
            field: "special_requirements",
            as: "special_requirements",
            type: "string",
          },
          { field: "organizer_email", as: "organizer_email", type: "string" },
          { field: "organizer_name", as: "organizer_name", type: "string" },
        ],
      ),

      // 3. AI venue and activity suggester
      aiNode(
        "3",
        "AI Suggest Venues and Activities",
        520,
        "You are an expert event planner. Based on the event type, headcount, budget, and preferences, suggest 3 venue options and 3 activity options. Each should include name, estimated cost, capacity, and why it fits. Respond JSON: {venue_option_1: string, venue_cost_1: number, venue_option_2: string, venue_cost_2: number, venue_option_3: string, venue_cost_3: number, activity_suggestions: string, theme_suggestion: string, catering_recommendation: string}",
        "Event type: {event_type}. Headcount: {headcount}. Budget: ${budget_min}-${budget_max}. Location: {location_pref}. Dietary: {dietary_restrictions}. Special: {special_requirements}. Suggest the best options.",
        [
          { field: "venue_option_1", as: "venue_option_1", type: "string" },
          { field: "venue_cost_1", as: "venue_cost_1", type: "number" },
          { field: "venue_option_2", as: "venue_option_2", type: "string" },
          { field: "venue_cost_2", as: "venue_cost_2", type: "number" },
          { field: "venue_option_3", as: "venue_option_3", type: "string" },
          { field: "venue_cost_3", as: "venue_cost_3", type: "number" },
          {
            field: "activity_suggestions",
            as: "activity_suggestions",
            type: "string",
          },
          { field: "theme_suggestion", as: "theme_suggestion", type: "string" },
          {
            field: "catering_recommendation",
            as: "catering_recommendation",
            type: "string",
          },
        ],
      ),

      // 4. Availability coordinator Google Calendar API
      n("4", "hitApi", "Check Team Availability via Google Calendar", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/freeBusy",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "timeMin", value: "{preferred_dates}T09:00:00Z" },
            { key: "timeMax", value: "{preferred_dates}T18:00:00Z" },
            { key: "items", value: '[{"id":"primary"}]' },
          ],
          variables: [
            {
              name: "calendars",
              customName: "availability_data",
              type: "string",
            },
          ],
        },
      }),

      // 5. Budget calculator runCode
      n("5", "runCode", "Calculate Event Budget Breakdown", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate detailed budget breakdown
const headcount = parseInt(vars.headcount || "10", 10);
const budgetMax = parseInt(vars.budget_max || "5000", 10);
const venueCost1 = parseInt(vars.venue_cost_1 || "0", 10);
const venueCost2 = parseInt(vars.venue_cost_2 || "0", 10);
const venueCost3 = parseInt(vars.venue_cost_3 || "0", 10);
const cateringPerHead = 35;
const cateringTotal = headcount * cateringPerHead;
const activitiesEstimate = Math.round(budgetMax * 0.2);
const miscBuffer = Math.round(budgetMax * 0.1);
const option1Total = venueCost1 + cateringTotal + activitiesEstimate + miscBuffer;
const option2Total = venueCost2 + cateringTotal + activitiesEstimate + miscBuffer;
const option3Total = venueCost3 + cateringTotal + activitiesEstimate + miscBuffer;
const withinBudget = [option1Total, option2Total, option3Total].filter(t => t <= budgetMax).length;
const bestOption = option1Total <= budgetMax ? "Option 1" : option2Total <= budgetMax ? "Option 2" : "Option 3";
return {
  catering_total: cateringTotal,
  activities_estimate: activitiesEstimate,
  option_1_total: option1Total,
  option_2_total: option2Total,
  option_3_total: option3Total,
  within_budget_count: withinBudget,
  recommended_option: bestOption,
  per_person_cost: Math.round(option1Total / headcount),
  budget_summary: "Option 1: $" + option1Total + " | Option 2: $" + option2Total + " | Option 3: $" + option3Total + " (Budget: $" + budgetMax + ")"
};`,
          outputVariables: [
            { name: "catering_total", type: "number" },
            { name: "activities_estimate", type: "number" },
            { name: "option_1_total", type: "number" },
            { name: "option_2_total", type: "number" },
            { name: "option_3_total", type: "number" },
            { name: "within_budget_count", type: "number" },
            { name: "recommended_option", type: "string" },
            { name: "per_person_cost", type: "number" },
            { name: "budget_summary", type: "string" },
          ],
        },
      }),

      // 6. Voting system Slack
      slackNode(
        "6",
        "Post Event Options for Team Vote",
        1240,
        "Team Event Planning — *{event_type}*\\nOrganized by {organizer_name} | {headcount} people\\n\\n:one: {venue_option_1} — ${option_1_total}\\n:two: {venue_option_2} — ${option_2_total}\\n:three: {venue_option_3} — ${option_3_total}\\n\\nActivities: {activity_suggestions}\\nTheme: {theme_suggestion}\\nCatering: {catering_recommendation} (${catering_total})\\n\\nReact with :one: :two: or :three: to vote!\\nRecommended: {recommended_option}",
      ),

      // 7. Booking executor API
      n("7", "hitApi", "Execute Venue Booking via API", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.venue-booking.com/v1/reservations",
          headers: [
            { key: "Authorization", value: "Bearer {venue_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "venue", value: "{venue_option_1}" },
            { key: "date", value: "{preferred_dates}" },
            { key: "headcount", value: "{headcount}" },
            { key: "event_type", value: "{event_type}" },
            { key: "dietary_notes", value: "{dietary_restrictions}" },
            { key: "special_requirements", value: "{special_requirements}" },
          ],
          variables: [
            {
              name: "reservation_id",
              customName: "booking_id",
              type: "string",
            },
            {
              name: "confirmation_code",
              customName: "confirmation_code",
              type: "string",
            },
            { name: "status", customName: "booking_status", type: "string" },
          ],
        },
      }),

      // 8. Logistics coordinator email
      emailNode(
        "8",
        "Email Event Logistics to Team",
        1720,
        "{organizer_email}",
        "Team Event Confirmed: {event_type} — {preferred_dates}",
        "<h2>Event Confirmed!</h2><p><strong>Type:</strong> {event_type}</p><p><strong>Date:</strong> {preferred_dates}</p><p><strong>Headcount:</strong> {headcount}</p><p><strong>Venue:</strong> {venue_option_1}</p><p><strong>Booking ID:</strong> {booking_id} | Confirmation: {confirmation_code}</p><h3>Budget Breakdown</h3><p>{budget_summary}</p><p>Per person: ${per_person_cost}</p><h3>Activities</h3><p>{activity_suggestions}</p><h3>Catering</h3><p>{catering_recommendation}</p><p>Dietary restrictions noted: {dietary_restrictions}</p><h3>Theme</h3><p>{theme_suggestion}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1442. TEAM WORKLOAD BALANCER (8 nodes)
  // ===================================================================

  "Team Workload Balancer": {
    summary:
      "Runs daily on schedule, fetches task assignments from Asana, fetches calendar commitments from Google Calendar, calculates workload per person with runCode, generates AI rebalance recommendations, alerts on imbalance via condition, executes rebalancing in Asana, and tracks workload trends in Sheets.",
    nodes: [
      trigger("Schedule: Daily workload balance check", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs every morning to analyze task distribution across team members, detect overloaded or underutilized individuals, and recommend or execute workload rebalancing",
        setup:
          "Configure Asana token and project GID for task data. Provide Google Calendar API credentials for calendar commitments. Set OpenAI API key for rebalance recommendations. Set Slack webhook for alerts. Configure Google Sheets for trend tracking.",
        inputFields: [],
      }),

      // 2. Task assignment fetch Asana API
      n("2", "hitApi", "Fetch Team Task Assignments from Asana", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://app.asana.com/api/1.0/projects/{asana_project_gid}/tasks?opt_fields=name,assignee.name,assignee.email,due_on,completed,custom_fields&limit=100",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "task_list", type: "string" },
            { name: "data.length", customName: "total_tasks", type: "number" },
          ],
        },
      }),

      // 3. Calendar commitment fetch Google Calendar API
      n("3", "hitApi", "Fetch Calendar Commitments from Google", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin={week_start}&timeMax={week_end}&singleEvents=true&maxResults=250",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "items",
              customName: "calendar_commitments",
              type: "string",
            },
            {
              name: "items.length",
              customName: "meeting_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. Workload calculator runCode
      n("4", "runCode", "Calculate Per-Person Workload Scores", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate workload score per team member
const tasks = JSON.parse(vars.task_list || "[]");
const meetings = parseInt(vars.meeting_count || "0", 10);
const totalTasks = parseInt(vars.total_tasks || "0", 10);
const workloadMap = {};
for (const task of tasks) {
  if (task.completed) continue;
  const assignee = (task.assignee && task.assignee.name) || "Unassigned";
  if (!workloadMap[assignee]) workloadMap[assignee] = { tasks: 0, overdue: 0, highPriority: 0 };
  workloadMap[assignee].tasks += 1;
  if (task.due_on && new Date(task.due_on) < new Date()) workloadMap[assignee].overdue += 1;
}
const members = Object.keys(workloadMap);
const memberCount = Math.max(members.length, 1);
const avgTasksPerPerson = Math.round(totalTasks / memberCount);
const overloaded = [];
const underloaded = [];
for (const m of members) {
  const score = workloadMap[m].tasks;
  if (score > avgTasksPerPerson * 1.5) overloaded.push(m + " (" + score + " tasks)");
  else if (score < avgTasksPerPerson * 0.5) underloaded.push(m + " (" + score + " tasks)");
}
const imbalanceDetected = overloaded.length > 0 ? "yes" : "no";
const meetingLoadPerPerson = Math.round(meetings / memberCount);
return {
  team_member_count: memberCount,
  avg_tasks_per_person: avgTasksPerPerson,
  overloaded_members: overloaded.join(", ") || "none",
  underloaded_members: underloaded.join(", ") || "none",
  imbalance_detected: imbalanceDetected,
  overloaded_count: overloaded.length,
  meeting_load_avg: meetingLoadPerPerson,
  workload_summary: memberCount + " members | avg " + avgTasksPerPerson + " tasks | " + overloaded.length + " overloaded | " + meetings + " meetings this week"
};`,
          outputVariables: [
            { name: "team_member_count", type: "number" },
            { name: "avg_tasks_per_person", type: "number" },
            { name: "overloaded_members", type: "string" },
            { name: "underloaded_members", type: "string" },
            { name: "imbalance_detected", type: "string" },
            { name: "overloaded_count", type: "number" },
            { name: "meeting_load_avg", type: "number" },
            { name: "workload_summary", type: "string" },
          ],
        },
      }),

      // 5. AI rebalance recommender
      aiNode(
        "5",
        "AI Recommend Workload Rebalancing",
        1000,
        "You are an agile team coach specializing in workload management. Based on the current workload distribution, recommend specific task reassignments to balance the team. Consider skills, availability, and task priority. Respond JSON: {recommendations: string, reassignment_count: number, priority_actions: string, coaching_note: string, estimated_improvement: string}",
        "Team: {team_member_count} members, avg {avg_tasks_per_person} tasks each. Overloaded: {overloaded_members}. Underloaded: {underloaded_members}. Meeting load avg: {meeting_load_avg}. Total tasks: {total_tasks}. Recommend specific rebalancing actions.",
        [
          {
            field: "recommendations",
            as: "rebalance_recommendations",
            type: "string",
          },
          {
            field: "reassignment_count",
            as: "reassignment_count",
            type: "number",
          },
          { field: "priority_actions", as: "priority_actions", type: "string" },
          { field: "coaching_note", as: "coaching_note", type: "string" },
          {
            field: "estimated_improvement",
            as: "estimated_improvement",
            type: "string",
          },
        ],
      ),

      // 6. Imbalance alert condition
      n("6", "condition", "Workload Imbalance Detected?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "imbalance_detected", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 7. Rebalance executor Asana API (True path)
      slackNode(
        "7",
        "Alert Team About Workload Imbalance",
        1480,
        ":warning: *Workload Imbalance Alert*\\n{workload_summary}\\n\\nOverloaded: {overloaded_members}\\nUnderloaded: {underloaded_members}\\n\\nRecommendations: {rebalance_recommendations}\\nPriority Actions: {priority_actions}\\nCoaching Note: {coaching_note}\\nEstimated Improvement: {estimated_improvement}",
        300,
      ),

      // 8. Workload trend tracker Sheets
      sheetsNode(
        "8",
        "Log Workload Trends to Sheets",
        1720,
        "WorkloadTrends",
        '[["{matrix_date}","{team_member_count}","{total_tasks}","{avg_tasks_per_person}","{overloaded_count}","{meeting_load_avg}","{imbalance_detected}","{reassignment_count}","{workload_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1443. THIRD PARTY SERVICE STATUS MONITOR (8 nodes)
  // ===================================================================

  "Third Party Service Status Monitor": {
    summary:
      "Runs every 5 minutes on schedule, scrapes service status pages via API, performs API health checks with runCode, detects status changes via condition, maps impact with runCode, dispatches alerts to Slack, triggers customer communication emails, and correlates incidents in Sheets.",
    nodes: [
      trigger("Schedule: Service status check every 5 min", "schedule", {
        schedule: "Every 5 minutes",
        howItStarts:
          "Runs every 5 minutes to check the operational status of critical third-party services (AWS, Stripe, Twilio, SendGrid, etc.) and detect degradation or outages",
        setup:
          "Configure status page URLs for each monitored service. Set API endpoints for direct health checks. Set Slack webhook for incident alerts. Configure email for customer communications. Provide Google Sheets for incident correlation tracking.",
        inputFields: [],
      }),

      // 2. Status page scraper API
      n("2", "hitApi", "Fetch Status from Third-Party Status Pages", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://status.aws.amazon.com/data.json",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "current", customName: "aws_status", type: "string" },
            {
              name: "archive",
              customName: "aws_recent_incidents",
              type: "string",
            },
          ],
        },
      }),

      // 3. API health check runCode
      n("3", "runCode", "Perform Direct API Health Checks", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Evaluate status data and simulate direct health checks
const awsStatus = (vars.aws_status || "").toLowerCase();
const timestamp = new Date().toISOString();
const services = [
  { name: "AWS", status: awsStatus.includes("disruption") ? "degraded" : "operational" },
  { name: "Stripe", status: "operational" },
  { name: "Twilio", status: "operational" },
  { name: "SendGrid", status: "operational" },
];
const degraded = services.filter(s => s.status !== "operational");
const operational = services.filter(s => s.status === "operational");
const overallStatus = degraded.length === 0 ? "all_clear" : degraded.length === 1 ? "partial_degradation" : "major_incident";
const statusChanged = degraded.length > 0 ? "yes" : "no";
return {
  overall_status: overallStatus,
  status_changed: statusChanged,
  degraded_services: degraded.map(s => s.name + ": " + s.status).join(", ") || "none",
  operational_services: operational.map(s => s.name).join(", "),
  degraded_count: degraded.length,
  total_monitored: services.length,
  check_timestamp: timestamp,
  services_summary: services.map(s => s.name + "=" + s.status).join(" | ")
};`,
          outputVariables: [
            { name: "overall_status", type: "string" },
            { name: "status_changed", type: "string" },
            { name: "degraded_services", type: "string" },
            { name: "operational_services", type: "string" },
            { name: "degraded_count", type: "number" },
            { name: "total_monitored", type: "number" },
            { name: "check_timestamp", type: "string" },
            { name: "services_summary", type: "string" },
          ],
        },
      }),

      // 4. Status change detector condition
      n("4", "condition", "Status Change or Degradation Detected?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "status_changed", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 5. Impact mapper runCode (True path)
      n("5", "runCode", "Map Service Degradation Impact", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map degraded services to business impact
const degraded = (vars.degraded_services || "none").split(", ");
const impactMap = {
  "AWS": { affected: "All services", severity: "critical", customers: "all" },
  "Stripe": { affected: "Payments, billing", severity: "high", customers: "paying" },
  "Twilio": { affected: "SMS, calls", severity: "medium", customers: "communication" },
  "SendGrid": { affected: "Email delivery", severity: "medium", customers: "email" },
};
let maxSeverity = "low";
const impacts = [];
const affectedFeatures = [];
for (const svc of degraded) {
  const name = svc.split(":")[0].trim();
  const impact = impactMap[name] || { affected: "Unknown", severity: "low", customers: "unknown" };
  impacts.push(name + " — " + impact.affected + " (" + impact.severity + ")");
  affectedFeatures.push(impact.affected);
  if (impact.severity === "critical") maxSeverity = "critical";
  else if (impact.severity === "high" && maxSeverity !== "critical") maxSeverity = "high";
  else if (impact.severity === "medium" && maxSeverity === "low") maxSeverity = "medium";
}
const notifyCustomers = maxSeverity === "critical" || maxSeverity === "high" ? "yes" : "no";
return {
  impact_summary: impacts.join(" | ") || "No impact",
  max_severity: maxSeverity,
  affected_features: affectedFeatures.join(", ") || "none",
  notify_customers: notifyCustomers,
  incident_id: "INC-" + Date.now().toString(36).toUpperCase()
};`,
          outputVariables: [
            { name: "impact_summary", type: "string" },
            { name: "max_severity", type: "string" },
            { name: "affected_features", type: "string" },
            { name: "notify_customers", type: "string" },
            { name: "incident_id", type: "string" },
          ],
        },
      }),

      // 6. Alert dispatcher Slack
      slackNode(
        "6",
        "Alert Team About Service Degradation",
        1240,
        ":rotating_light: *Service Status Alert — {incident_id}*\\nSeverity: {max_severity}\\nDegraded Services: {degraded_services}\\nImpact: {impact_summary}\\nAffected Features: {affected_features}\\nStatus: {overall_status}\\nTimestamp: {check_timestamp}",
      ),

      // 7. Customer communication trigger email
      emailNode(
        "7",
        "Send Customer Impact Notification",
        1480,
        "{customer_comms_email}",
        "[{incident_id}] Service Advisory — {max_severity} impact on {affected_features}",
        "<h2>Service Status Advisory</h2><p><strong>Incident:</strong> {incident_id}</p><p><strong>Severity:</strong> {max_severity}</p><p><strong>Affected Services:</strong> {degraded_services}</p><p><strong>Impact:</strong> {impact_summary}</p><p><strong>Features Affected:</strong> {affected_features}</p><p>We are actively monitoring the situation and will provide updates. Current operational services: {operational_services}.</p>",
      ),

      // 8. Incident correlation Sheets
      sheetsNode(
        "8",
        "Log Incident to Correlation Tracker",
        1720,
        "ServiceIncidents",
        '[["{check_timestamp}","{incident_id}","{overall_status}","{degraded_services}","{max_severity}","{affected_features}","{impact_summary}","{notify_customers}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1444. TIME TRACKING REMINDER BOT (7 nodes)
  // ===================================================================

  "Time Tracking Reminder Bot": {
    summary:
      "Runs daily at end of day on schedule, checks timesheet status from API, identifies gaps with runCode, generates a friendly AI reminder, delivers reminders via Slack, routes compliance escalations via condition, and updates the compliance dashboard in Sheets.",
    nodes: [
      trigger("Schedule: Daily EOD time tracking reminder", "schedule", {
        schedule: "Every weekday at 5:00 PM",
        howItStarts:
          "Runs at end of each business day to check who has not submitted their time entries, identify gaps or incomplete submissions, and send friendly reminders with compliance escalation for repeat offenders",
        setup:
          "Configure time tracking API credentials (Harvest, Toggl, or custom). Set OpenAI API key for friendly reminders. Set Slack webhook for reminder delivery. Configure Google Sheets for compliance tracking.",
        inputFields: [],
      }),

      // 2. Timesheet status check API
      n("2", "hitApi", "Check Timesheet Submissions via API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.timetracking.com/v1/timesheets?date={today_date}&status=all",
          headers: [
            { key: "Authorization", value: "Bearer {timetracking_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "timesheet_data", type: "string" },
            {
              name: "data.length",
              customName: "submission_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. Gap identifier runCode
      n("3", "runCode", "Identify Timesheet Gaps and Missing Entries", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Identify team members with missing or incomplete timesheets
const timesheets = JSON.parse(vars.timesheet_data || "[]");
const submitted = timesheets.filter(t => t.status === "submitted" || t.status === "approved");
const missing = timesheets.filter(t => t.status === "missing" || t.status === "incomplete");
const teamSize = 10;
const submittedCount = submitted.length;
const missingCount = Math.max(0, teamSize - submittedCount);
const missingNames = missing.map(t => t.employee_name || t.email || "Unknown").join(", ") || "None identified";
const complianceRate = Math.round((submittedCount / teamSize) * 100);
const needsReminder = missingCount > 0 ? "yes" : "no";
const needsEscalation = complianceRate < 70 ? "yes" : "no";
const today = new Date().toISOString().split("T")[0];
const dayOfWeek = new Date().toLocaleDateString("en-US", { weekday: "long" });
return {
  submitted_count: submittedCount,
  missing_count: missingCount,
  missing_names: missingNames,
  compliance_rate: complianceRate,
  needs_reminder: needsReminder,
  needs_escalation: needsEscalation,
  check_date: today,
  day_of_week: dayOfWeek
};`,
          outputVariables: [
            { name: "submitted_count", type: "number" },
            { name: "missing_count", type: "number" },
            { name: "missing_names", type: "string" },
            { name: "compliance_rate", type: "number" },
            { name: "needs_reminder", type: "string" },
            { name: "needs_escalation", type: "string" },
            { name: "check_date", type: "string" },
            { name: "day_of_week", type: "string" },
          ],
        },
      }),

      // 4. AI friendly reminder
      aiNode(
        "4",
        "AI Generate Friendly Time Reminder",
        760,
        "You are a friendly office assistant. Write a warm, non-judgmental reminder for team members who have not submitted their timesheets. Be encouraging, mention it is end of day, and include a gentle nudge about compliance. Keep it light and professional. Respond JSON: {reminder_message: string, subject_line: string, fun_fact: string}",
        "It is {day_of_week}, {check_date}. {missing_count} team members have not submitted timesheets. Compliance rate: {compliance_rate}%. Missing: {missing_names}. Write a friendly reminder that does not feel like a reprimand.",
        [
          { field: "reminder_message", as: "reminder_message", type: "string" },
          { field: "subject_line", as: "reminder_subject", type: "string" },
          { field: "fun_fact", as: "fun_fact", type: "string" },
        ],
      ),

      // 5. Reminder delivery Slack
      slackNode(
        "5",
        "Send Time Tracking Reminder to Slack",
        1000,
        ":clock5: *Time Tracking Reminder — {day_of_week}*\\n{reminder_message}\\n\\nSubmitted: {submitted_count} | Missing: {missing_count} | Compliance: {compliance_rate}%\\n:bulb: {fun_fact}",
      ),

      // 6. Compliance escalation condition
      n("6", "condition", "Compliance Below Threshold?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "needs_escalation", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 7. Compliance dashboard Sheets
      sheetsNode(
        "7",
        "Update Compliance Dashboard in Sheets",
        1480,
        "TimeCompliance",
        '[["{check_date}","{day_of_week}","{submitted_count}","{missing_count}","{compliance_rate}","{missing_names}","{needs_escalation}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1445. TRANSACTIONAL SMS GATEWAY (8 nodes)
  // ===================================================================

  "Transactional SMS Gateway": {
    summary:
      "Receives SMS requests via webhook, extracts SMS data with AI, validates requests with runCode, processes templates with runCode, dispatches via Twilio API, monitors delivery status via condition, handles failover via backup carrier API, and logs compliance to Sheets.",
    nodes: [
      trigger("Webhook: Transactional SMS request", "webhook_app", {
        source: "application_server",
        event: "sms.send_requested",
        howItStarts:
          "Fires when any application service requests a transactional SMS — OTP codes, order confirmations, appointment reminders, security alerts, or delivery notifications",
        setup:
          "Configure webhook from your application SMS bus. Set OpenAI API key for data extraction. Provide Twilio Account SID, Auth Token, and sending number. Configure backup carrier API credentials. Set Google Sheets for compliance logging.",
        inputFields: [],
      }),

      // 2. AI extract SMS data
      aiNode(
        "2",
        "AI Extract SMS Request Data",
        280,
        "You are an SMS gateway parser. Extract the recipient phone number, message template type, template variables, sender ID, priority, and any opt-out checking requirements from the raw webhook payload. Respond JSON: {recipient_phone: string, template_type: string, template_vars: string, sender_id: string, priority: string, requires_opt_out_check: string, message_category: string, request_id: string, source_app: string}",
        "Raw webhook payload: {webhook_payload}. Extract all SMS request details regardless of field naming.",
        [
          { field: "recipient_phone", as: "recipient_phone", type: "string" },
          { field: "template_type", as: "template_type", type: "string" },
          { field: "template_vars", as: "template_vars", type: "string" },
          { field: "sender_id", as: "sender_id", type: "string" },
          { field: "priority", as: "sms_priority", type: "string" },
          {
            field: "requires_opt_out_check",
            as: "requires_opt_out_check",
            type: "string",
          },
          { field: "message_category", as: "message_category", type: "string" },
          { field: "request_id", as: "request_id", type: "string" },
          { field: "source_app", as: "source_app", type: "string" },
        ],
      ),

      // 3. Request validator runCode
      n("3", "runCode", "Validate SMS Request Compliance", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate SMS request for compliance and formatting
const phone = (vars.recipient_phone || "").replace(/[^+\\d]/g, "");
const category = (vars.message_category || "transactional").toLowerCase();
const priority = (vars.sms_priority || "normal").toLowerCase();
const isValidPhone = phone.length >= 10 && phone.length <= 15;
const isTransactional = category === "transactional" || category === "otp" || category === "security";
const needsOptOut = !isTransactional;
const formattedPhone = phone.startsWith("+") ? phone : "+1" + phone;
const requestValid = isValidPhone ? "yes" : "no";
const rateLimit = priority === "critical" ? "bypass" : "standard";
return {
  formatted_phone: formattedPhone,
  request_valid: requestValid,
  is_transactional: isTransactional ? "yes" : "no",
  needs_opt_out: needsOptOut ? "yes" : "no",
  rate_limit_tier: rateLimit,
  validation_errors: isValidPhone ? "none" : "Invalid phone number format",
  validated_at: new Date().toISOString()
};`,
          outputVariables: [
            { name: "formatted_phone", type: "string" },
            { name: "request_valid", type: "string" },
            { name: "is_transactional", type: "string" },
            { name: "needs_opt_out", type: "string" },
            { name: "rate_limit_tier", type: "string" },
            { name: "validation_errors", type: "string" },
            { name: "validated_at", type: "string" },
          ],
        },
      }),

      // 4. Template engine runCode
      n("4", "runCode", "Process SMS Template", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Process SMS template with variables
const templateType = (vars.template_type || "generic").toLowerCase();
const templateVars = JSON.parse(vars.template_vars || "{}");
const templates = {
  otp: "Your verification code is " + (templateVars.code || "######") + ". It expires in " + (templateVars.expiry || "10") + " minutes. Do not share this code.",
  order_confirmation: "Order " + (templateVars.order_id || "N/A") + " confirmed! Total: " + (templateVars.total || "$0.00") + ". Track at: " + (templateVars.tracking_url || "N/A"),
  appointment: "Reminder: Your appointment is on " + (templateVars.date || "N/A") + " at " + (templateVars.time || "N/A") + ". Reply C to confirm or R to reschedule.",
  delivery: "Your package " + (templateVars.tracking_id || "N/A") + " is out for delivery. ETA: " + (templateVars.eta || "today") + ".",
  security: "Security alert: " + (templateVars.alert || "Unusual activity") + " detected on your account. If this was not you, call " + (templateVars.support_number || "support") + " immediately.",
  generic: templateVars.message || "You have a new notification. Please check your account.",
};
const messageBody = templates[templateType] || templates.generic;
const charCount = messageBody.length;
const segmentCount = Math.ceil(charCount / 160);
return {
  sms_body: messageBody,
  char_count: charCount,
  segment_count: segmentCount,
  template_used: templateType,
  template_processed: "yes"
};`,
          outputVariables: [
            { name: "sms_body", type: "string" },
            { name: "char_count", type: "number" },
            { name: "segment_count", type: "number" },
            { name: "template_used", type: "string" },
            { name: "template_processed", type: "string" },
          ],
        },
      }),

      // 5. Primary carrier dispatch Twilio API
      n("5", "hitApi", "Send SMS via Twilio", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.twilio.com/2010-04-01/Accounts/{twilio_account_sid}/Messages.json",
          headers: [
            { key: "Authorization", value: "Basic {twilio_auth_base64}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "To", value: "{formatted_phone}" },
            { key: "From", value: "{twilio_from_number}" },
            { key: "Body", value: "{sms_body}" },
          ],
          variables: [
            { name: "sid", customName: "twilio_message_sid", type: "string" },
            { name: "status", customName: "twilio_status", type: "string" },
            {
              name: "error_code",
              customName: "twilio_error_code",
              type: "string",
            },
          ],
        },
      }),

      // 6. Delivery status monitor condition
      n("6", "condition", "Twilio Delivery Successful?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "twilio_status", operator: "eq", value: "queued" },
          ],
        },
      }),

      // 7. Failover handler API (False path)
      n(
        "7",
        "hitApi",
        "Send via Backup Carrier (Failover)",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.backup-sms-carrier.com/v1/messages",
            headers: [
              { key: "Authorization", value: "Bearer {backup_carrier_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "to", value: "{formatted_phone}" },
              { key: "body", value: "{sms_body}" },
              { key: "sender", value: "{sender_id}" },
            ],
            variables: [
              { name: "id", customName: "backup_message_id", type: "string" },
              { name: "status", customName: "backup_status", type: "string" },
            ],
          },
        },
        300,
      ),

      // 8. Compliance logger Sheets
      sheetsNode(
        "8",
        "Log SMS to Compliance Tracker",
        1720,
        "SMSCompliance",
        '[["{validated_at}","{request_id}","{source_app}","{formatted_phone}","{template_used}","{message_category}","{segment_count}","{twilio_message_sid}","{twilio_status}","{is_transactional}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1446. TRANSLATION BOT (7 nodes)
  // ===================================================================

  "Translation Bot": {
    summary:
      "Receives translation requests via webhook, extracts text data with AI, detects language with runCode, performs contextual AI translation, enforces glossary terms with runCode, evaluates quality via condition, and delivers the result to Slack.",
    nodes: [
      trigger("Webhook: Translation request received", "webhook_app", {
        source: "communication_platform",
        event: "translation.requested",
        howItStarts:
          "Fires when a team member requests a translation via Slack command, form, or API — including the source text, target language, context, and any domain-specific terminology requirements",
        setup:
          "Configure webhook from Slack slash command or translation request form. Set OpenAI API key for language detection and translation. Set Slack webhook for result delivery.",
        inputFields: [],
      }),

      // 2. AI extract text data
      aiNode(
        "2",
        "AI Extract Translation Request Data",
        280,
        "You are a translation request parser. Extract the source text, target language, context (business, technical, casual, legal), any glossary terms to preserve, and the requester from the raw webhook payload. Respond JSON: {source_text: string, target_language: string, context: string, glossary_terms: string, requester_name: string, requester_channel: string, formality_level: string, preserve_formatting: string}",
        "Raw webhook payload: {webhook_payload}. Extract all translation request details regardless of format.",
        [
          { field: "source_text", as: "source_text", type: "string" },
          { field: "target_language", as: "target_language", type: "string" },
          { field: "context", as: "translation_context", type: "string" },
          { field: "glossary_terms", as: "glossary_terms", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          {
            field: "requester_channel",
            as: "requester_channel",
            type: "string",
          },
          { field: "formality_level", as: "formality_level", type: "string" },
          {
            field: "preserve_formatting",
            as: "preserve_formatting",
            type: "string",
          },
        ],
      ),

      // 3. Language detection runCode
      n("3", "runCode", "Detect Source Language and Prepare Context", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Detect source language from text characteristics
const text = vars.source_text || "";
const targetLang = (vars.target_language || "english").toLowerCase();
const wordCount = text.split(/\\s+/).filter(Boolean).length;
const charCount = text.length;
// Simple heuristic language detection
const hasKanji = /[\\u4e00-\\u9faf]/.test(text);
const hasCyrillic = /[\\u0400-\\u04FF]/.test(text);
const hasArabic = /[\\u0600-\\u06FF]/.test(text);
const hasKorean = /[\\uAC00-\\uD7AF]/.test(text);
const hasDevanagari = /[\\u0900-\\u097F]/.test(text);
let detectedLang = "english";
if (hasKanji) detectedLang = "japanese";
else if (hasCyrillic) detectedLang = "russian";
else if (hasArabic) detectedLang = "arabic";
else if (hasKorean) detectedLang = "korean";
else if (hasDevanagari) detectedLang = "hindi";
const sameLanguage = detectedLang === targetLang ? "yes" : "no";
const estimatedComplexity = wordCount > 200 ? "high" : wordCount > 50 ? "medium" : "low";
return {
  detected_language: detectedLang,
  word_count: wordCount,
  char_count: charCount,
  same_language: sameLanguage,
  translation_complexity: estimatedComplexity,
  language_pair: detectedLang + " -> " + targetLang
};`,
          outputVariables: [
            { name: "detected_language", type: "string" },
            { name: "word_count", type: "number" },
            { name: "char_count", type: "number" },
            { name: "same_language", type: "string" },
            { name: "translation_complexity", type: "string" },
            { name: "language_pair", type: "string" },
          ],
        },
      }),

      // 4. AI contextual translation
      aiNode(
        "4",
        "AI Perform Contextual Translation",
        760,
        "You are an expert multilingual translator. Translate the given text into the target language while preserving the original meaning, tone, and context. Respect domain-specific terminology from the provided glossary. Match the requested formality level. Provide a quality confidence score. Respond JSON: {translated_text: string, back_translation: string, quality_score: number, translator_notes: string, glossary_applied: string, alternative_phrasing: string}",
        "Translate from {detected_language} to {target_language}. Context: {translation_context}. Formality: {formality_level}. Glossary terms to preserve: {glossary_terms}. Source text: {source_text}. Provide a high-quality contextual translation.",
        [
          { field: "translated_text", as: "translated_text", type: "string" },
          { field: "back_translation", as: "back_translation", type: "string" },
          { field: "quality_score", as: "quality_score", type: "number" },
          { field: "translator_notes", as: "translator_notes", type: "string" },
          { field: "glossary_applied", as: "glossary_applied", type: "string" },
          {
            field: "alternative_phrasing",
            as: "alternative_phrasing",
            type: "string",
          },
        ],
      ),

      // 5. Glossary enforcement runCode
      n("5", "runCode", "Enforce Glossary Terms in Translation", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Verify and enforce glossary terms in the translation
const translated = vars.translated_text || "";
const glossaryRaw = vars.glossary_terms || "";
const glossaryTerms = glossaryRaw.split(",").map(t => t.trim()).filter(Boolean);
let enforced = translated;
let termsEnforced = 0;
let termsMissing = [];
for (const term of glossaryTerms) {
  const parts = term.split("=");
  if (parts.length === 2) {
    const original = parts[0].trim();
    const required = parts[1].trim();
    if (!enforced.includes(required)) {
      termsMissing.push(original + " -> " + required);
    } else {
      termsEnforced += 1;
    }
  }
}
const glossaryCompliance = glossaryTerms.length > 0 ? Math.round((termsEnforced / glossaryTerms.length) * 100) : 100;
const qualityScore = parseInt(vars.quality_score || "0", 10);
const passesQuality = qualityScore >= 80 && glossaryCompliance >= 80 ? "yes" : "no";
return {
  enforced_translation: enforced,
  terms_enforced: termsEnforced,
  terms_missing: termsMissing.join(", ") || "none",
  glossary_compliance: glossaryCompliance,
  quality_pass: passesQuality,
  final_quality: Math.round((qualityScore + glossaryCompliance) / 2)
};`,
          outputVariables: [
            { name: "enforced_translation", type: "string" },
            { name: "terms_enforced", type: "number" },
            { name: "terms_missing", type: "string" },
            { name: "glossary_compliance", type: "number" },
            { name: "quality_pass", type: "string" },
            { name: "final_quality", type: "number" },
          ],
        },
      }),

      // 6. Quality score condition
      n("6", "condition", "Translation Quality Passes Threshold?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "quality_pass", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 7. Result delivery Slack
      slackNode(
        "7",
        "Deliver Translation Result to Slack",
        1480,
        "Translation Complete for *{requester_name}* ({language_pair})\\nQuality: {final_quality}/100 | Complexity: {translation_complexity}\\n\\n*Original ({detected_language}):*\\n{source_text}\\n\\n*Translation ({target_language}):*\\n{enforced_translation}\\n\\n*Translator Notes:* {translator_notes}\\n*Glossary Compliance:* {glossary_compliance}% | Missing: {terms_missing}\\n*Alternative:* {alternative_phrasing}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1447. TRAVEL REQUEST APPROVAL WORKFLOW (9 nodes)
  // ===================================================================

  "Travel Request Approval Workflow": {
    summary:
      "Receives travel requests via webhook, extracts travel data with AI, checks policy compliance with runCode, verifies budget availability with runCode, optimizes costs with AI, routes by approval level via condition, notifies the approver by email, checks the approval decision via condition, and triggers booking confirmation by email.",
    nodes: [
      trigger("Webhook: Travel request submitted", "webhook_app", {
        source: "travel_portal",
        event: "travel.request_submitted",
        howItStarts:
          "Fires when an employee submits a travel request through the company travel portal, including destination, dates, purpose, estimated costs, and traveler details",
        setup:
          "Configure webhook from your travel request portal. Set OpenAI API key for cost optimization. Configure email for approval notifications and booking confirmations. Set budget thresholds for approval routing.",
        inputFields: [],
      }),

      // 2. AI extract travel data
      aiNode(
        "2",
        "AI Extract Travel Request Data",
        280,
        "You are a travel management assistant. Extract the traveler name, email, destination, travel dates, purpose, estimated flight cost, hotel cost, per diem, total budget, and manager email from the raw webhook payload. Respond JSON: {traveler_name: string, traveler_email: string, destination: string, departure_date: string, return_date: string, purpose: string, flight_estimate: number, hotel_estimate: number, per_diem_estimate: number, total_estimate: number, manager_email: string, department: string, travel_class: string}",
        "Raw webhook payload: {webhook_payload}. Extract all travel request details regardless of field naming.",
        [
          { field: "traveler_name", as: "traveler_name", type: "string" },
          { field: "traveler_email", as: "traveler_email", type: "string" },
          { field: "destination", as: "destination", type: "string" },
          { field: "departure_date", as: "departure_date", type: "string" },
          { field: "return_date", as: "return_date", type: "string" },
          { field: "purpose", as: "travel_purpose", type: "string" },
          { field: "flight_estimate", as: "flight_estimate", type: "number" },
          { field: "hotel_estimate", as: "hotel_estimate", type: "number" },
          {
            field: "per_diem_estimate",
            as: "per_diem_estimate",
            type: "number",
          },
          { field: "total_estimate", as: "total_estimate", type: "number" },
          { field: "manager_email", as: "manager_email", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "travel_class", as: "travel_class", type: "string" },
        ],
      ),

      // 3. Policy compliance check runCode
      n("3", "runCode", "Check Travel Policy Compliance", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check travel request against company policy
const totalEst = parseInt(vars.total_estimate || "0", 10);
const flightEst = parseInt(vars.flight_estimate || "0", 10);
const hotelEst = parseInt(vars.hotel_estimate || "0", 10);
const travelClass = (vars.travel_class || "economy").toLowerCase();
const departure = new Date(vars.departure_date || Date.now());
const now = new Date();
const advanceBookingDays = Math.floor((departure - now) / (1000 * 60 * 60 * 24));
const violations = [];
if (travelClass === "business" && totalEst < 5000) violations.push("Business class requires total trip > $5000");
if (travelClass === "first") violations.push("First class travel not permitted by policy");
if (advanceBookingDays < 7) violations.push("Travel booked less than 7 days in advance");
if (hotelEst > 300 * 5) violations.push("Hotel exceeds $300/night policy limit");
if (flightEst > 2000 && travelClass === "economy") violations.push("Economy flight exceeds $2000 threshold — review needed");
const policyCompliant = violations.length === 0 ? "yes" : "no";
const violationList = violations.join("; ") || "none";
const riskLevel = violations.length > 2 ? "high" : violations.length > 0 ? "medium" : "low";
return {
  policy_compliant: policyCompliant,
  violations: violationList,
  violation_count: violations.length,
  advance_booking_days: advanceBookingDays,
  policy_risk_level: riskLevel,
  request_id: "TRV-" + Date.now().toString(36).toUpperCase()
};`,
          outputVariables: [
            { name: "policy_compliant", type: "string" },
            { name: "violations", type: "string" },
            { name: "violation_count", type: "number" },
            { name: "advance_booking_days", type: "number" },
            { name: "policy_risk_level", type: "string" },
            { name: "request_id", type: "string" },
          ],
        },
      }),

      // 4. Budget availability check runCode
      n("4", "runCode", "Check Department Budget Availability", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check if department budget can accommodate travel request
const totalEst = parseInt(vars.total_estimate || "0", 10);
const department = vars.department || "general";
// Simulated department budgets
const budgets = {
  engineering: 50000, sales: 80000, marketing: 40000, executive: 100000, general: 30000
};
const deptBudget = budgets[department.toLowerCase()] || budgets.general;
const budgetRemaining = deptBudget;
const budgetSufficient = totalEst <= budgetRemaining ? "yes" : "no";
const budgetUtilization = Math.round((totalEst / deptBudget) * 100);
const approvalLevel = totalEst > 10000 ? "vp" : totalEst > 3000 ? "director" : "manager";
const autoApprove = totalEst <= 500 && vars.policy_compliant === "yes" ? "yes" : "no";
return {
  budget_sufficient: budgetSufficient,
  dept_budget_total: deptBudget,
  budget_utilization_pct: budgetUtilization,
  approval_level: approvalLevel,
  auto_approve: autoApprove,
  budget_summary: department + " budget: $" + deptBudget + " | Request: $" + totalEst + " (" + budgetUtilization + "% of budget)"
};`,
          outputVariables: [
            { name: "budget_sufficient", type: "string" },
            { name: "dept_budget_total", type: "number" },
            { name: "budget_utilization_pct", type: "number" },
            { name: "approval_level", type: "string" },
            { name: "auto_approve", type: "string" },
            { name: "budget_summary", type: "string" },
          ],
        },
      }),

      // 5. AI cost optimizer
      aiNode(
        "5",
        "AI Optimize Travel Costs",
        1000,
        "You are a corporate travel cost optimization expert. Analyze the travel request and suggest ways to reduce costs without sacrificing the trip purpose. Compare estimated costs to market averages, suggest alternative dates, routes, or accommodations. Respond JSON: {savings_suggestions: string, estimated_savings: number, alternative_flights: string, alternative_hotels: string, optimized_total: number, cost_rating: string}",
        "Traveler: {traveler_name}. Destination: {destination}. Dates: {departure_date} to {return_date}. Purpose: {travel_purpose}. Flight: ${flight_estimate}. Hotel: ${hotel_estimate}. Per diem: ${per_diem_estimate}. Total: ${total_estimate}. Class: {travel_class}. Advance booking: {advance_booking_days} days. Suggest optimizations.",
        [
          {
            field: "savings_suggestions",
            as: "savings_suggestions",
            type: "string",
          },
          {
            field: "estimated_savings",
            as: "estimated_savings",
            type: "number",
          },
          {
            field: "alternative_flights",
            as: "alternative_flights",
            type: "string",
          },
          {
            field: "alternative_hotels",
            as: "alternative_hotels",
            type: "string",
          },
          { field: "optimized_total", as: "optimized_total", type: "number" },
          { field: "cost_rating", as: "cost_rating", type: "string" },
        ],
      ),

      // 6. Approval router condition
      n("6", "condition", "Auto-Approve Eligible?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "auto_approve", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 7. Approver notification email (False path - needs manual approval)
      emailNode(
        "7",
        "Send Approval Request to Manager",
        1480,
        "{manager_email}",
        "[{request_id}] Travel Approval Required: {traveler_name} to {destination}",
        "<h2>Travel Approval Request — {request_id}</h2><p><strong>Traveler:</strong> {traveler_name} ({traveler_email})</p><p><strong>Department:</strong> {department}</p><p><strong>Destination:</strong> {destination}</p><p><strong>Dates:</strong> {departure_date} to {return_date}</p><p><strong>Purpose:</strong> {travel_purpose}</p><h3>Cost Breakdown</h3><ul><li>Flight: ${flight_estimate} ({travel_class})</li><li>Hotel: ${hotel_estimate}</li><li>Per Diem: ${per_diem_estimate}</li><li><strong>Total: ${total_estimate}</strong></li></ul><h3>Policy Check</h3><p>Compliant: {policy_compliant} | Violations: {violations}</p><h3>Budget</h3><p>{budget_summary}</p><h3>Cost Optimization</h3><p>Potential Savings: ${estimated_savings}</p><p>{savings_suggestions}</p><p>Optimized Total: ${optimized_total} | Rating: {cost_rating}</p><p><strong>Approval Level Required:</strong> {approval_level}</p><p>Please reply APPROVE or DENY with comments.</p>",
        300,
      ),

      // 8. Approval decision condition
      n("8", "condition", "Budget Sufficient for Trip?", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "budget_sufficient", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 9. Booking trigger email (auto-approved or approved path)
      emailNode(
        "9",
        "Send Booking Confirmation to Traveler",
        1960,
        "{traveler_email}",
        "[{request_id}] Travel Request Approved — {destination}",
        "<h2>Travel Request Approved!</h2><p>Hi {traveler_name},</p><p>Your travel request {request_id} has been processed.</p><p><strong>Destination:</strong> {destination}</p><p><strong>Dates:</strong> {departure_date} to {return_date}</p><p><strong>Approved Budget:</strong> ${total_estimate}</p><p><strong>Purpose:</strong> {travel_purpose}</p><h3>Next Steps</h3><ul><li>Book flights within the approved budget</li><li>Reserve hotel accommodations</li><li>Submit receipts within 5 business days of return</li></ul><h3>Cost Tips</h3><p>{savings_suggestions}</p><p>Safe travels!</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "9", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
      e("8", "9", "output_1"),
    ],
  },
};
